import gql from "graphql-tag";

// Pricing Explainer (#7197) — shared selection set for the `explainer` field on
// ItineraryItemPricing. Interpolated into both the live getPricing query and the
// saved-itinerary pricing blocks. graphql-tag concatenates plain-string
// interpolations verbatim before parsing, so this is a single source of truth.
// `commission` and `circuitDiscount` are only populated on the saved-itinerary path
// (both resolve to null on getPricing — circuit discount is resolved itinerary-wide,
// so a single-service price build has no context); requesting them on both is valid.
// The whole field is null when the
// `pricing_explainer` access setting is off or the service has not been recalculated
// since the feature was enabled (no backfill).
const EXPLAINER_FIELDS = `
  explainer {
    recalculatedAt
    recalculatedByUserId
    schemaVersion
    flags {
      skipMarkup
      skipCircuitDiscount
      autoPrice
      useHistoricalRates
      yearShift
    }
    serviceLine {
      rateType
      rateTypeName
      adults
      children
      infants
      qty
      passComps
      supplierComps
      duration
      currencySupplier
      currencyAgent
      currencyBase
      roeCost
      roeSell
      totalSupplier
      totalBase
      totalAgent
      unitPrices {
        kind
        supplier
        agent
      }
    }
    extraLine {
      percentPrice
      percentBasis
      parentUnitSell
      parentUnitBuy
      percentDay
      rateBase
      pax
      autoPrice
      isChildOnly
      isInfantOnly
      isMandatory
      included
      optional
      supplierCommission
    }
    markupApplied {
      source
      appliedPercent
      dayFrom
      dayTo
      candidateCount
      markupRecord {
        id
        department
        groupName
        groupId
        destinationName
        destinationId
        tagsList
        validFrom
        validTo
        useDateRange
        itiCreatedFrom
        itiCreatedTo
        historicalOnly
        percent
      }
    }
    agentMarkup {
      appliedPercent
      valueApplied
    }
    supplierDiscount {
      applied
      valueCost
      valueSell
      currency
      discountRecord {
        id
        description
        agentGroupId
        groupName
        validFrom
        validTo
        bookBeforeDate
        advanceDays
        round
        book
        free
        percent
      }
    }
    commission {
      source
      percent
      valueApplied
      currency
      commissionRecord {
        id
        serviceType
        department
        groupId
        percent
      }
    }
    circuitDiscount {
      applied
      valueCost
      valueSell
      candidateCount
      record {
        id
        description
        percent
        percentCost
        validFrom
        validTo
        minStay
        maxStay
        minSuppliers
        maxSuppliers
        bookAfter
        bookBy
        days
        groupId
        groupName
      }
    }
    summary {
      totalSupplier
      totalBase
      totalAgent
      markupValueTotal
      discountValueTotal
      circuitDiscountCost
      circuitDiscountSell
      netProfit
      markupPercent
      marginPercent
      profitIncludesCircuitDiscount
    }
  }
`;

export const SIGNIN_USER_MUTATION = gql`
  mutation signInUser($username: String!, $pass: String!, $twoFaCode: String) {
    signInUser(username: $username, password: $pass, tfAuthCode: $twoFaCode) {
      token
      id
      agentId
      username
      name
      accessSettings {
        groupName
        settingName
        settingValue
      }
      agent {
        financeSettings {
          currencyCode
        }
        languageId
        name
        companyCode
      }
      owner {
        id
        name
      }
      aiModules
      email
      logo
      department
      brand
      destinationId
      domain
      vueSettings
      config {
        dateFormat
        defaultLanguageId
        maxUsers
        isSupervisor
        isSupplier
      }
    }
  }
`;
export const ALL_GTWEB_TEMPLATES_SEARCH_QUERY = gql`
  query allGtwebTemplatesSearch($after: String, $filter: GtWebTemplateFilterType) {
    gtwebTemplates(first: 50, after: $after, filter: $filter) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          name
          destinationName
          agentName
          pricingDate
        }
      }
    }
  }
`;

export const ALL_GTWEB_RATES_SEARCH_QUERY = gql`
  query allGtwebRatesSearch($after: String, $filter: RatesImportFilterType) {
    ratesImports(first: 50, after: $after, filter: $filter) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          name
          fileType
          createdAt
          importedAt
          importedBy
        }
      }
    }
  }
`;
export const ALL_ITINERARIES_SEARCH_QUERY = gql`
  query allItinerariesList(
    $after: String
    $filter: ItineraryFilterType
    $sort: SortOrderType
    $first: Int
    $withServiceGroups: Boolean!
    $withLeadClientTripsCount: Boolean!
  ) {
    itineraries(first: $first, after: $after, filter: $filter, orderBy: $sort) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          reference
          startDate
          endDate
          totalPrice
          paid
          commission
          commissionAdjustment
          netCommission
          supplierCommission
          supplierCommissionReceived
          supplierCommissionBalance
          lastPaymentDate
          firstSalesInvoiceDate
          balance
          grossBalance
          brand
          leadSource
          arrivalHotel
          bookedByName
          currencyCode
          agentName
          agentId

          agent {
            id
            agentType
            companyCode
          }
          sourceItinerary {
            id
            reference
          }
          bookingStatus
          department
          destinationName
          createdAt
          clientFirstName
          clientSurname
          isLocked
          hasServiceGroups @include(if: $withServiceGroups)
          client {
            id
            name
            surname
            telephone
            mobile
            address
            itiCount @include(if: $withLeadClientTripsCount)
            nationality
          }
          clientName
          clientEmail
          clientReference
          internalReference
          internalStatus
          adults
          children
          infants
          pax {
            adults
            children
            infants
          }
          hasClientInvoice
          hasPurchaseInvoice
          createdByName
          profit {
            netProfit
            markup
            margin
            grossProfit
          }
          salesInvoices {
            id
            number
          }
        }
      }
    }
  }
`;
export const ALL_ITINERARIES_DESTINATIONS_QUERY = gql`
  query allItinerariesDestinations($after: String, $filter: ItineraryFilterType) {
    itineraries(first: 100, after: $after, filter: $filter) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          destinationName
        }
      }
    }
  }
`;
export const ALL_ITINERARIES_STAFF_QUERY = gql`
  query allItinerariesStaff($after: String, $filter: ItineraryFilterType) {
    itineraries(first: 100, after: $after, filter: $filter) {
      edges {
        node {
          id
          totalPrice
          agentName
          createdBy {
            name
          }
        }
      }
    }
  }
`;
export const USERS_SEARCH_QUERY = gql`
  query usersSearch($after: String, $filter: UserFilterType) {
    users(first: 100, after: $after, filter: $filter) {
      edges {
        node {
          accessSettings {
            groupName
            settingName
            settingValue
          }
          config {
            sendItineraryNotification
          }
          id
          username
          agentId
          agentName
          name
          email
          lastLogin
          telephone
          department
          typeId
          customFields {
            totalCount
            edges {
              node {
                content
                name
                owner
                typeId
              }
            }
          }
        }
      }
    }
  }
`;
export const AGENT_STAFF_LIST = gql`
  query agentStaffList($after: String, $filter: UserFilterType) {
    users(first: 500, after: $after, filter: $filter) {
      edges {
        node {
          id
          username
          agentId
          agentName
          name
          email
          lastLogin
          isActive
          notInUse
          isSupervisor
          emailVerified
          notes
        }
      }
    }
  }
`;
export const USER_TYPES_QUERY = gql`
  query userTypes($after: String) {
    userTypes(after: $after) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;
export const USER_UPDATE_MUTATION = gql`
  mutation updateStaff(
    $id: ID!
    $name: String!
    $username: String!
    $department: String
    $email: String!
    $telephone: String
    $password: String
    $typeId: ID
    $destinationId: ID
    $brand: String
    $accessSettings: [AccessSettingInput]
    $config: UserConfigSettingsInput
    $customFields: [CustomFieldInput]
    $isActive: Boolean
    $notInUse: Boolean
    $isSupervisor: Boolean
    $emailVerified: Boolean
    $notes: String
  ) {
    updateUser(
      id: $id
      userInput: {
        name: $name
        username: $username
        department: $department
        email: $email
        telephone: $telephone
        password: $password
        typeId: $typeId
        accessSettings: $accessSettings
        config: $config
        customFields: $customFields
        brand: $brand
        destinationId: $destinationId
        isActive: $isActive
        notInUse: $notInUse
        isSupervisor: $isSupervisor
        emailVerified: $emailVerified
        notes: $notes
      }
    ) {
      accessSettings {
        groupName
        settingName
        settingValue
      }
      config {
        sendItineraryNotification
      }
      agentId
      agentName
      department
      email
      id
      lastLogin
      name
      telephone
      username
      brand
      destinationId
      isActive
      notInUse
      notes
    }
  }
`;
export const USER_CREATE_MUTATION = gql`
  mutation userCreate(
    $agentId: ID!
    $name: String!
    $username: String!
    $department: String
    $email: String!
    $telephone: String
    $password: String
    $typeId: ID
    $destinationId: ID
    $brand: String
    $accessSettings: [AccessSettingInput]
    $config: UserConfigSettingsInput
    $isActive: Boolean
    $notInUse: Boolean
    $notes: String
  ) {
    createUser(
      agentId: $agentId
      userInput: {
        name: $name
        username: $username
        department: $department
        email: $email
        telephone: $telephone
        password: $password
        typeId: $typeId
        accessSettings: $accessSettings
        config: $config
        brand: $brand
        destinationId: $destinationId
        isActive: $isActive
        notInUse: $notInUse
        notes: $notes
      }
    ) {
      accessSettings {
        groupName
        settingName
        settingValue
      }
      config {
        sendItineraryNotification
      }
      agentId
      agentName
      department
      email
      id
      lastLogin
      name
      telephone
      username
      brand
      destinationId
    }
  }
`;
export const USER_DELETE_MUTATION = gql`
  mutation userDelete($id: ID!) {
    deleteUser(id: $id) {
      id
    }
  }
`;
export const SEND_CLIENT_MESSAGE = gql`
  mutation sendContactClientMsg($input: ContactClientInputType!) {
    contactClient(input: $input) {
      success
      error
    }
  }
`;
export const ITINERARY_RECORD = gql`
  query itineraryDetails($id: ID!) {
    itinerary(id: $id) {
      id
      destinationName
      destination {
        id
      }
      reference
      clientReference
      brand
      leadSource
      commissionAdjustment
      netCommission
      grossBalance
      clientEmail
      agent {
        id
        email
        telephone
        name
      }
      pax {
        adults
        children
        infants
      }
      profit {
        commission
        commissionAdjustment
        netCommission
        netProfit
        grossProfit
        totalCost
        totalSell
        margin
        markup
        tomsTax
        supplierCommission
        supplierCommissionBalance
        supplierCommissionReceived
      }
      startDate
      endDate
      totalPrice
      currencyCode
      clientName
      client {
        id
      }
      featuredImage
      internalReference
      bookingStatus
      internalStatus
      balance
      commission
      paid
      agentName
      bookedByName
      bookedBy {
        id
      }
      createdAt
      department
      createdBy {
        name
        email
      }
      logs {
        edges {
          node {
            userName
            eventDate
            eventTime
            message
            messageType
            marker
          }
        }
      }
      routes {
        edges {
          node {
            id
            name
            code
            description
            coordinates
            tags
            color
            date
            sequence
            pois {
              edges {
                node {
                  id
                  name
                  lng
                  lat
                  description
                  radius
                  audio
                  type {
                    id
                    name
                    iconName
                    iconSVG
                  }
                }
              }
            }
          }
        }
      }
      documents {
        edges {
          node {
            id
            name
            createdAt
            createdBy {
              name
            }
            downloadURL
            editURL
          }
        }
      }
      serviceGroups {
        edges {
          node {
            id
            name
            description
            participants {
              edges {
                node {
                  id
                  title
                  name
                  surname
                }
              }
            }
            items {
              edges {
                node {
                  id
                  itineraryItemId
                  adults
                  children
                  infants
                }
              }
            }
          }
        }
      }
      comps {
        edges {
          node {
            id
            pass
            free
            paxFrom
            paxTo
            supplierCompId
            supplierId
            serviceId
            service {
              id
              name
            }
            supplier {
              id
              name
            }
          }
        }
      }
      participants {
        edges {
          node {
            number
            title
            mobile
            name
            surname
            age
            email
            isLeader
            clientType
            clientStatus
            rooms
            groupLabel
            client {
              id
              address
              name
              surname
              email
              mobile
              telephone
              address
              notes
              birthDate
            }
          }
        }
      }
      items {
        edges {
          node {
            id
            orderNo
            service {
              id
              stdOccupancy
              altName
              noVoucher
              typeName
            }
            supplier {
              id
              region
              headOffice {
                id
                name
              }
              noVoucher
            }
            supplierName
            supplierDestination
            supplierRegion
            serviceName
            serviceTypeName
            startDate
            endDate
            totalPrice
            totalCostInBase
            totalSellInBase
            toms
            margin
            markup
            rateTypeCode
            status
            statusId
            qty
            isOptional
            isIncluded
            isHidden
            includeCommission
            tags
            taxCode
            fromDeparture
            openGroupDepartureItemId
            hasApprovedPurchaseInvoice
            hasPurchaseInvoice
            boardBasis
            pax {
              adults
              children
              infants
            }
            customPricing
            businessModel
            businessModelName
            supplierCommission
            supplierCommissionValue
            supplierComp
            passComp
            yearShift
            isHistoricalRate
            commission
            profit
            netProfit
            pricing {
              adultBuy
              adultSell
              childBuy
              childSell
              infantBuy
              infantSell
              itemBuy
              itemSell
              rateTypeCode
              ratePlan
              currencyBuy
              currencyBuyExchangeRate
              currencySell
              currencySellExchangeRate
              itemBased
              dayBased
              discountCost
              discountSell
              discountInfo
              circuitDiscountCost
              circuitDiscountSell
              discountDescription
              totalBuy

              pricingDays {
                allocation
                adultSell
                childSell
                infantSell
                itemSell
                discountPercent
                discountCost
                discountSell
              }
              logs {
                date
                time
                username
                rateType
                adultBuy
                adultSell
                childBuy
                childSell
                infantBuy
                infantSell
                itemBuy
                itemSell
                currencyBuy
                currencySell
                currencyBuyExchangeRate
                currencySellExchangeRate
                supplierComp
                passComp
              }
              ${EXPLAINER_FIELDS}
            }
            supplierCommissionReceipts {
              amount
            }
            source {
              name
              id
              sourceId
              confirmationRef
            }
            extras {
              id
              uid
              name
              adults
              children
              infants
              qty
              startDate
              endDate
              totalSell
              totalBuy
              currencySell
              currencyBuy
              isChildOnly
              percentChild
              isIncluded
              isOptional
              isInfantOnly
              isMandatory
              rateBase
              rateTypeCode
              cost
              sell
              percentPrice
              customPricing
              isHidden
              status
              statusId
              commission
              includeCommission
              supplierCommission
              totalCostInBase
              totalSellInBase
              discount
              discountCost
              discountSell
              currencySellExchangeRate
              currencyBuyExchangeRate
              toms
              orderNo
              ${EXPLAINER_FIELDS}
              profit
              netProfit
              margin
              markup
              remarks {
                pickupPoint
                dropoffPoint
                pickupTime
                dropoffTime
                timeFrom
                agentNotes
                supplierNotes
                voucherNotes
                flightNo
                confirmationNo
                roomNumbers
                internalNotes
              }
            }
            remarks {
              pickupPoint
              dropoffPoint
              pickupTime
              dropoffTime
              timeFrom
              agentNotes
              supplierNotes
              voucherNotes
              flightNo
              confirmationNo
              roomNumbers
              internalNotes
            }
          }
        }
        totalCount
      }
    }
  }
`;
export const ALL_SUPPLIERS_SEARCH_QUERY = gql`
  query allSuppliersSearch(
    $after: String
    $filter: SupplierFilterType
    $sort: SortOrderType
    $first: Int
  ) {
    suppliers(first: $first, after: $after, filter: $filter, orderBy: $sort) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          name
          rating
          code
          region
          type
          boardBasis
          destinationName
          destination {
            id
            name
          }
          headOffice {
            id
            name
          }
          email
          accountsEmail
          telephone
          address
          notInUse
          marketPlace
          currencyCode
        }
      }
    }
  }
`;
export const AVAILABLE_REPORTS_SEARCH_QUERY = gql`
  query availableReportsSearch {
    availableReports {
      totalCount
      edges {
        node {
          id
          name
          formats
          filters {
            label
            name
            value
            filterName
          }
        }
      }
    }
  }
`;
export const SUPPLIERS_CONTENT_SEARCH_QUERY = gql`
  query suppliersContentSearch($after: String, $filter: SupplierFilterType) {
    suppliers(first: 100, after: $after, filter: $filter) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          name
          serviceType
          allContents {
            id
            name
            description
            imgURL
            languageId
          }
          allServices {
            id
            name
            allContents {
              id
              name
              description
              imgURL
              languageId
            }
          }
        }
      }
    }
  }
`;
export const NON_ALLOCATED_SERVICES_SEARCH_QUERY = gql`
  query nonAllocatedServicesSearch($itineraryId: ID!, $after: String) {
    contactSupplierItems: nonAllocatedServices(
      itineraryId: $itineraryId
      after: $after
    ) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          date
          groupId
          isExtra
          serviceId
          serviceName
          serviceType
          supplierEmail
          supplierName
          supplier {
            headOffice {
              email
              name
            }
          }
        }
      }
    }
  }
`;
export const AMENDABLE_SERVICES_SEARCH_QUERY = gql`
  query amendableServicesSearch($itineraryId: ID!, $after: String) {
    contactSupplierItems: amendableServices(
      itineraryId: $itineraryId
      after: $after
    ) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          date
          groupId
          isExtra
          serviceId
          serviceName
          serviceType
          supplierEmail
          supplierName
          supplier {
            headOffice {
              email
              name
            }
          }
        }
      }
    }
  }
`;
export const CANCELABLE_SERVICES_SEARCH_QUERY = gql`
  query cancelableServicesSearch($itineraryId: ID!, $after: String) {
    contactSupplierItems: cancellableServices(
      itineraryId: $itineraryId
      after: $after
    ) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          date
          groupId
          isExtra
          serviceId
          serviceName
          serviceType
          supplierEmail
          supplierName
          supplier {
            headOffice {
              email
              name
            }
          }
        }
      }
    }
  }
`;
export const SEND_CONFIRMATION_MESSAGE_MUTATION = gql`
  mutation sendConfirmationMessage($itineraryId: ID!, $items: [ContactSuppliersItemInputType]!) {
    sendConfirmationMessage(itineraryId: $itineraryId, items: $items) {
      error
      success
    }
  }
`;
export const SEND_AMENDMENT_MESSAGE_MUTATION = gql`
  mutation sendAmendmentMessage($itineraryId: ID!, $items: [ContactSuppliersItemInputType]!) {
    sendAmendmentMessage(itineraryId: $itineraryId, items: $items) {
      error
      success
    }
  }
`;
export const SEND_CANCELLATION_MESSAGE_MUTATION = gql`
  mutation sendCancellationMessage($itineraryId: ID!, $items: [ContactSuppliersItemInputType]!) {
    sendCancellationMessage(itineraryId: $itineraryId, items: $items) {
      error
      success
    }
  }
`;
export const PAYMENT_TYPE_UPDATE_MUTATION = gql`
  mutation paymentTypeUpdate($paymentTypeId: ID!, $name: String) {
    updatePaymentType(id: $paymentTypeId, paymenttypeInput: { name: $name }) {
      id
      name
    }
  }
`;

export const ALL_CLIENT_TITLES_SEARCH_QUERY = gql`
  query allClientTitlesSearch($after: String, $sort: SortOrderType) {
    clientTitles(first: 50, after: $after, orderBy: $sort) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          title
          notInUse
        }
      }
    }
  }
`;

export const ALL_EXPORT_LOGS_SEARCH_QUERY = gql`
  query allExportLogsSearch(
    $after: String
    $sort: SortOrderType
    $first: Int
    $filter: ExportLogFilterType
  ) {
    exportLogs(first: $first, after: $after, orderBy: $sort, filter: $filter) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          name
          userName
          createdAt
          results
          recordsCount
          criteria
          createdAtTime
        }
      }
    }
  }
`;

export const ALL_CLIENT_TYPES_SEARCH_QUERY = gql`
  query allClientTypesSearch($after: String, $sort: SortOrderType) {
    clientTypes(first: 50, after: $after, orderBy: $sort) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          name
          isDefault
          notInUse
        }
      }
    }
  }
`;

export const ALL_AGENT_TYPES_SEARCH_QUERY = gql`
  query allAgentTypesSearch($after: String, $sort: SortOrderType) {
    agentTypes(first: 50, after: $after, orderBy: $sort) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          name
          notInUse
        }
      }
    }
  }
`;

export const ALL_CLIENT_STATUSES_SEARCH_QUERY = gql`
  query allClientStatusesSearch($after: String, $sort: SortOrderType) {
    clientStatuses(first: 50, after: $after, orderBy: $sort) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          name
          isDefault
          notInUse
        }
      }
    }
  }
`;

export const FIRST_CLIENT_SEARCH_QUERY = gql`
  query firstClientSearch {
    clients(first: 1) {
      edges {
        node {
          id
          name
          customFields {
            edges {
              node {
                name
                owner
                typeId
              }
            }
          }
        }
      }
    }
  }
`;
export const ALL_CLIENTS_SEARCH_QUERY = gql`
  query allClientsSearch(
    $after: String
    $filter: ClientFilterType
    $sort: SortOrderType
    $first: Int
  ) {
    clients(first: $first, after: $after, filter: $filter, orderBy: $sort) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          title
          name
          surname
          email
          telephone
          mobile
          address
          code
          clientStatusName
          clientTypeName
          language
          nationality
          notes
        }
      }
    }
  }
`;
export const ALL_ITINERARY_TASKS_SEARCH_QUERY = gql`
  query allItineraryTasksSearch(
    $after: String
    $filter: ItineraryTaskFilterType
    $first: Int
    $sort: SortOrderType
  ) {
    itineraryTasks(
      first: $first
      after: $after
      filter: $filter
      orderBy: $sort
    ) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          actualFinish
          actualStart
          actualTime
          assignedByName
          assignedToName
          createdAt
          estimatedStart
          estimatedTime
          priority
          itinerary {
            id
            internalStatus
            startDate
          }
          itineraryRef
          name
          nextAction
          nextActionDueDate
          notes
          project
          projectId
          taskStatus
          taskType
          updatedAt
          updatedBy
          user {
            id
          }
          userName
          taskActions {
            edges {
              node {
                id
                name
                isComplete
                updatedAt
                userName
                dueDate
                notes
              }
            }
          }
        }
      }
    }
  }
`;
export const ALL_HISTORIES_SEARCH_QUERY = gql`
  query allHistoriesSearch(
    $after: String
    $filter: HistoryFilterType
    $first: Int
    $sort: SortOrderType
  ) {
    itineraryHistories(
      first: $first
      after: $after
      filter: $filter
      orderBy: $sort
    ) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          groupId
          itineraryReference
          allocationUID
          date
          serviceName
          supplierName
          adults
          children
          infants
          qty
          total
          totalSell
          sellCurrencyCode
          buyCurrencyCode
          status
          rateTypeCode
          rateTypeName
          isCustomPrice
          serviceTypeName
          supplierId
          isHistoricalRate
          totalCostInBase
          basis
          pickupTime
          dropoffTime
          voucherNotes
          confirmationNo
          salesInvoices
          purchaseInvoices
          #isIncluded
          #isOptional
          supplier {
            destinationName
            region
            nominalCode
            purchaseNominalCode
            headOffice {
              name
            }
          }
          itinerary {
            id
            client {
              id
            }
            internalStatus
            internalReference
            department
            startDate
            endDate
            agentName
            agentId
            bookingStatus
            clientName
            destinationName
            bookedByName
            createdByName
            createdAt
            notes
            brand
            leadSource
          }
        }
      }
    }
  }
`;
export const ALL_AGENTS_SEARCH_QUERY = gql`
  query allAgentsSearch(
    $after: String
    $first: Int
    $filter: AgentFilterType
    $sort: SortOrderType
  ) {
    agents(first: $first, after: $after, filter: $filter, orderBy: $sort) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          name
          companyCode
          agentType
          department
          email
          address
          city
          state
          postCode
          notInUse
          externalDomain
          region
          notes
          telephone
          language {
            id
            name
          }
        }
      }
    }
  }
`;
export const FIRST_SUPPLIER_SEARCH_QUERY = gql`
  query firstSupplierSearch {
    suppliers(first: 1) {
      edges {
        node {
          id
          name
          customFields {
            edges {
              node {
                name
                owner
                typeId
              }
            }
          }
        }
      }
    }
  }
`;

export const FIRST_AGENT_SEARCH_QUERY = gql`
  query firstAgentSearch {
    agents(first: 1) {
      edges {
        node {
          id
          name
          companyCode
          department
          email
          address
          city
          state
          postCode
          customFields {
            edges {
              node {
                name
                owner
                typeId
              }
            }
          }
        }
      }
    }
  }
`;
export const ALL_SALES_INVOICES_SEARCH_QUERY = gql`
  query allSalesInvoicesSearch(
    $after: String
    $filter: SalesInvoiceFilterType
    $sort: SortOrderType
    $first: Int
  ) {
    salesInvoices(
      first: $first
      after: $after
      filter: $filter
      orderBy: $sort
    ) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          agentName
          agentAccountCode
          agent {
            id
          }
          number
          clientName
          client {
            id
          }
          itinerary {
            id
            balance
            bookedByName
            destinationName
          }
          itineraryInternalRef
          itineraryReference
          itineraryStartDate
          itineraryStatus
          itineraryInternalStatus
          department
          itineraryBrand
          amount
          exported
          exportedAt
          exportedByName
          currencyCode
          approved
          externalReference
          createdAt
          paid
          balance
          approvedAt
          approvedByName
          invoicedByName
        }
      }
    }
  }
`;
export const SALES_INVOICE_RECORD_QUERY = gql`
  query salesInvoiceData($id: ID!) {
    salesInvoice(id: $id) {
      agent {
        address
        email
        id
        name
        telephone
      }
      client {
        address
        email
        id
        mobile
        name
        surname
        telephone
        title
      }
      itinerary {
        id
        currencyCode
        totalPrice
        paid
        balance
        startDate
        endDate
        reference
      }
      itineraryItems {
        edges {
          node {
            supplierName
            serviceName
            startDate
            endDate
            totalPrice
            rateTypeCode
            rateTypeName
            isIncluded
            qty
            pax {
              adults
              children
              infants
            }
            pricing {
              currencySell
            }
            extras {
              name
              startDate
              endDate
              qty
              adults
              children
              infants
              rateTypeCode
              rateTypeName
              totalSell
              isIncluded
              isOptional
            }
          }
        }
      }
      createdAt
      currencyCode
      id
      itineraryReference
      itineraryStartDate
      number
      paid
      approved
      amount
      externalReference
      exported
    }
  }
`;
export const PURCHASE_INVOICE_PAYMENT_CREATE_MUTATION = gql`
  mutation purchaseInvoicePaymentCreate(
    $purchaseInvoiceId: ID!
    $supplierName: String
    $notes: String
    $type: String
    $reference: String
    $paymentDate: DateTimeType
    $amount: Float!
    $exchangeRate: Float
    $currencyCode: String
    $vatCode: String
    $vatPercent: Float
    $paymentMethodId: ID
  ) {
    invoicePaymentMutation: createPurchaseInvoicePayment(
      purchaseInvoiceId: $purchaseInvoiceId
      paymentInput: {
        supplierName: $supplierName
        notes: $notes
        type: $type
        reference: $reference
        paymentDate: $paymentDate
        amount: $amount
        exchangeRate: $exchangeRate
        currencyCode: $currencyCode
        vatCode: $vatCode
        vatPercent: $vatPercent
        paymentMethodId: $paymentMethodId
      }
    ) {
      amount
      currencyCode
      id
      itineraryReference
      notes
      paymentDate
      paymentType
      exchangeRate
      reference
      supplierName
      piNumber
      taxCode
      paymentMethodId
    }
  }
`;
export const PURCHASE_INVOICE_PAYMENT_RECORD_QUERY = gql`
  query purchaseInvoicePaymentRecord($id: ID!) {
    purchaseInvoicePayment(id: $id) {
      amount
      currencyCode
      id
      itineraryReference
      notes
      paymentDate
      paymentType
      exchangeRate
      reference
      supplierName
      piNumber
      taxCode
      exported
      paymentMethodId
    }
  }
`;
export const PURCHASE_INVOICE_PAYMENT_UPDATE_MUTATION = gql`
  mutation purchaseInvoicePaymentUpdate(
    $id: ID!
    $supplierName: String
    $notes: String
    $type: String
    $reference: String
    $paymentDate: DateTimeType
    $amount: Float!
    $exchangeRate: Float
    $currencyCode: String
    $vatCode: String
    $vatPercent: Float
    $exported: Boolean
    $paymentMethodId: ID
  ) {
    invoicePaymentMutation: updatePurchaseInvoicePayment(
      id: $id
      paymentInput: {
        supplierName: $supplierName
        notes: $notes
        type: $type
        reference: $reference
        paymentDate: $paymentDate
        amount: $amount
        exchangeRate: $exchangeRate
        currencyCode: $currencyCode
        vatCode: $vatCode
        vatPercent: $vatPercent
        exported: $exported
        paymentMethodId: $paymentMethodId
      }
    ) {
      id
      supplierName
      notes
      reference
      paymentDate
      amount
      exchangeRate
      currencyCode
      paymentType
      paymentMethodId
    }
  }
`;
export const SALES_INVOICE_UPDATE_MUTATION = gql`
  mutation salesInvoiceUpdate(
    $id: ID!
    $approved: Boolean
    $externalReference: String
    $exported: Boolean
  ) {
    updateSalesInvoice(
      id: $id
      salesInvoiceInput: {
        approved: $approved
        exported: $exported
        externalReference: $externalReference
      }
    ) {
      id
      approved
    }
  }
`;
export const ALL_PURCHASE_INVOICES_SEARCH_QUERY = gql`
  query allPurchaseInvoicesSearch(
    $after: String
    $filter: PurchaseInvoiceFilterType
    $sort: SortOrderType
    $first: Int
  ) {
    purchaseInvoices(
      first: $first
      after: $after
      filter: $filter
      orderBy: $sort
    ) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          number
          clientName
          agentName
          agentAccountCode
          agent {
            id
          }
          department
          clientName
          supplierName
          headOfficeName
          supplier {
            id
            serviceType
            headOfficeId
            destinationName
            paymentMethod
            payeeName
            email
            accountsEmail
          }
          itineraryInternalRef
          itineraryStatus
          itineraryInternalStatus
          itineraryBrand
          itinerary {
            id
            internalReference
            clientName
            clientReference
            agentId
            client {
              id
            }
          }
          itineraryReference
          itineraryStartDate
          paymentDueDate
          amount
          totalAdjustments
          currencyCode
          exchangeRate
          approved
          approvedAt
          externalReference
          createdAt
          vatAmount
          netAmount
          firstServiceStartDate
          supplierInvoiceDate
          paid
          balance
          approvedByName
          invoicedByName
          approvedBy {
            username
          }
          exported
          exportedAt
          exportedByName
          notes
          payments {
            id
          }
        }
      }
    }
  }
`;
export const ITINERARY_INVOICE_SUPPLIERS_QUERY = gql`
  query itineraryInvoiceSuppliers($itineraryId: ID!) {
    itineraryInvoiceSuppliers(itineraryId: $itineraryId) {
      edges {
        node {
          id
          supplierName
          serviceType
        }
      }
    }
  }
`;
export const ALL_PAYMENTS_SEARCH_QUERY = gql`
  query allPaymentsSearch(
    $after: String
    $filter: PaymentFilterType
    $sort: SortOrderType
    $first: Int
  ) {
    payments(first: $first, after: $after, filter: $filter, orderBy: $sort) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          agentName
          agentAccountCode
          payerName
          payerSurname
          amount
          createdAt
          createdByName
          createdBy {
            name
          }
          currencyCode
          department
          exported
          exportedAt
          exportedByName
          exchangeRate
          itineraryInternalRef
          itineraryBrand
          batchNo
          itinerary {
            internalReference
            id
            agentId
            bookedByName
            internalStatus
            bookingStatus
            clientName
            clientSurname
          }
          itineraryReference
          itineraryStartDate
          lodgementDate
          lodgementReference
          notes
          type
          paymentMethodId
          paymentMethodName
        }
      }
    }
  }
`;
export const ALL_PAYMENTS_TYPES_SEARCH_QUERY = gql`
  query allPaymentsTypesSearch {
    paymentTypes {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;
export const PAYMENT_TYPE_CREATE_MUTATION = gql`
  mutation paymentTypeCreate($name: String) {
    createPaymentType(paymenttypeInput: { name: $name }) {
      id
      name
    }
  }
`;

export const PAYMENT_TYPE_DELETE_MUTATION = gql`
  mutation paymentTypeDelete($paymentTypeId: ID!) {
    deletePaymentType(id: $paymentTypeId) {
      id
    }
  }
`;
export const ALL_PAYMENT_TYPES_SEARCH_QUERY = gql`
  query allPaymentTypesSearch {
    paymentTypes {
      totalCount
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;
export const ALL_PURCHASE_INVOICE_PAYMENTS_SEARCH_QUERY = gql`
  query allPurchaseInvoicePaymentsSearch(
    $after: String
    $filter: PurchaseInvoicePaymentFilterType
    $sort: SortOrderType
    $first: Int
  ) {
    purchaseInvoicePayments(
      first: $first
      after: $after
      filter: $filter
      orderBy: $sort
    ) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          clientName
          clientSurname
          supplierName
          itineraryReference
          itineraryBrand
          itineraryDepartment
          agentName
          piNumber
          externalReference
          reference
          amount
          currencyCode
          exchangeRate
          exported
          exportedAt
          exportedByName
          posted
          paymentDate
          createdAt
          paymentType
          payeeName
          batchNo
          notes
          paymentMethodId
          paymentMethodName
          itineraryStartDate
          headOfficeName
          createdByName
          itineraryInternalStatus
          invoice {
            id
            firstServiceStartDate
            supplier {
              id
              payeeName
              accountsEmail
              email
            }
          }
          itinerary {
            id
          }
        }
      }
    }
  }
`;
export const PURCHASE_INVOICE_RECORD_QUERY = gql`
  query purchaseInvoiceRecord($id: ID!) {
    purchaseInvoice(id: $id) {
      agent {
        address
        email
        id
        name
        telephone
      }
      itinerary {
        id
        currencyCode
        totalPrice
        paid
        balance
        startDate
        endDate
        reference
        histories {
          serviceName
          date
          qty
          adults
          children
          infants
          rateTypeCode
          total
          sellCurrencyCode
          supplier {
            id
          }
        }
      }
      supplier {
        id
        name
        address
        telephone
        email
        currencyCode
      }
      payments {
        id
        amount
        currencyCode
        paymentType
        reference
        paymentDate
        supplierName
        itineraryReference
        notes
        exchangeRate
        taxCode
        paymentMethodId
      }
      adjustments {
        edges {
          node {
            id
            amount
            vatCode
            vatPercent
            username
            createdAt
            notes
          }
        }
      }
      createdAt
      currencyCode
      id
      itineraryReference
      itineraryStartDate
      number
      paid
      balance
      approved
      exported
      amount
      supplierInvoiceDate
      supplierInvoiceNumber
      supplierName
      notes
      itineraryItems {
        edges {
          node {
            supplierName
            supplier {
              id
            }
            serviceName
            startDate
            endDate
            totalPrice
            rateTypeCode
            qty
            pax {
              adults
              children
              infants
            }
            isIncluded
            pricing {
              currencyBuy
              currencyBuyExchangeRate
              totalBuy
            }
            totalCostInSupplierCurrency
            costRoeInSupplierCurrency
            extras {
              name
              startDate
              endDate
              qty
              adults
              children
              infants
              rateTypeCode
              totalBuy
              currencyBuy
              currencyBuyExchangeRate
              totalCostInSupplierCurrency
              costRoeInSupplierCurrency
              isIncluded
              isOptional
            }
          }
        }
      }
    }
  }
`;
export const PURCHASE_INVOICE_UPDATE_MUTATION = gql`
  mutation purchaseInvoiceUpdate(
    $id: ID!
    $approved: Boolean
    $supplierInvoiceNumber: String
    $supplierInvoiceDate: DateTimeType
    $exported: Boolean
    $notes: String
  ) {
    updatePurchaseInvoice(
      id: $id
      purchaseInvoiceInput: {
        approved: $approved
        supplierInvoiceNumber: $supplierInvoiceNumber
        supplierInvoiceDate: $supplierInvoiceDate
        exported: $exported
        notes: $notes
      }
    ) {
      id
      approved
      supplierInvoiceNumber
      supplierInvoiceDate
      exported
    }
  }
`;
export const SUPPLIER_RECORD_QUERY = gql`
  query supplierRecord($id: ID!, $serviceFilter: ServiceFilterType) {
    supplier(id: $id) {
      id
      isUsed
      serviceType
      name
      altName
      typeName
      code
      address
      email
      website
      geolocation
      rating
      languageId
      currencyCode
      defaultRateTypeCode
      boardBasis
      accountCode
      nominalCode
      purchaseNominalCode
      email2
      telephone
      fax
      region
      notInUse
      marketPlace
      autoApprove
      isHeadOffice
      noLetter
      noVoucher
      extrasChildPrice
      address
      notes
      cancellationPolicy
      headOfficeId
      pickup
      dropoff
      zone
      tags
      payeeName
      accountsEmail
      paymentMethod
      extras {
        edges {
          node {
            id
            agentGroupName
            commission
            cost
            currencyCode
            discount
            groupId
            markup
            name
            percentPrice
            rateTypeCode
            sell
            isChildOnly
            percentChild
            isInfantOnly
            capacityChange
            service {
              id
              name
              code
              typeName
              minOccupancy
              maxOccupancy
              stdOccupancy
            }
            typeId
            validFrom
            validTo
          }
        }
      }
      paymentTerms {
        edges {
          node {
            isPercent
            days
            amount
            altAmount
            termType
          }
        }
      }
      destination {
        id
        name
      }
      services(filter: $serviceFilter) {
        totalCount
        edges {
          node {
            id
            name
            altName
            code
            typeName
            minOccupancy
            maxOccupancy
            stdOccupancy
            notInUse
            marketPlace
            noReport
            noVoucher
            skipMarkup
            skipCircuitDiscount
            skipAgentCommission
            taxCode
            pickupPoint
            dropoffPoint
            pickupTime
            dropoffTime
            address
            coordinates
          }
        }
      }
      customFields {
        totalCount
        edges {
          node {
            content
            name
            owner
            typeId
          }
        }
      }
      supplierPortalUser {
        id
        name
        username
        email
      }
    }
  }
`;
export const SUPPLIER_RATES_QUERY = gql`
  query supplierPriceList($supplierId: ID!, $filter: SupplierRateFilterType) {
    supplierRates(supplierId: $supplierId, filter: $filter) {
      totalCount
      edges {
        node {
          id
          maxStay
          minStay
          minPax
          maxPax
          markup
          ratePlan
          rateTypeCode
          exception
          service {
            id
            name
            code
          }
          agentGroup {
            id
            name
          }
          remarks
          currencyCode
          adultBuy1
          adultSell1
          adultBuy2
          adultSell2
          adultBuy3
          adultSell3
          adultBuy4
          adultSell4
          adultBuy5
          adultSell5
          adultBuy6
          adultSell6
          adultBuy7
          adultSell7
          childBuy1
          childSell1
          childBuy2
          childSell2
          childBuy3
          childSell3
          childBuy4
          childSell4
          childBuy5
          childSell5
          childBuy6
          childSell6
          childBuy7
          childSell7
          infantBuy1
          infantSell1
          infantBuy2
          infantSell2
          infantBuy3
          infantSell3
          infantBuy4
          infantSell4
          infantBuy5
          infantSell5
          infantBuy6
          infantSell6
          infantBuy7
          infantSell7
          dateFrom
          dateTo
          groupId
          businessModel
          supplierCommission
          marketPlace
          api
        }
      }
    }
  }
`;
export const SUPPLIER_UPDATE_MUTATION = gql`
  mutation updateSupplier(
    $id: ID!
    $name: String
    $typeName: String
    $altName: String
    $code: String
    $email: String
    $website: String
    $geolocation: String
    $destinationId: ID
    $languageId: ID
    $rating: String
    $currencyCode: String
    $defaultRateTypeCode: String
    $boardBasis: String
    $accountCode: String
    $nominalCode: String
    $purchaseNominalCode: String
    $email2: String
    $telephone: String
    $fax: String
    $region: String
    $notInUse: Boolean
    $isHeadOffice: Boolean
    $extrasChildPrice: Boolean
    $address: String
    $notes: String
    $pickup: String
    $dropoff: String
    $cancellationPolicy: String
    $headOfficeId: ID
    $customFields: [CustomFieldInput]
    $paymentTerms: [PaymentTermInput]
    $zone: String
    $tags: String
    $noLetter: Boolean
    $noVoucher: Boolean
    $marketPlace: Boolean
    $autoApprove: Boolean
    $paymentMethod: String
    $payeeName: String
    $accountsEmail: String
    $propagateMarketPlace: Boolean
  ) {
    updateSupplier(
      id: $id
      supplierInput: {
        name: $name
        typeName: $typeName
        altName: $altName
        code: $code
        email: $email
        geolocation: $geolocation
        website: $website
        destinationId: $destinationId
        languageId: $languageId
        rating: $rating
        currencyCode: $currencyCode
        defaultRateTypeCode: $defaultRateTypeCode
        boardBasis: $boardBasis
        accountCode: $accountCode
        nominalCode: $nominalCode
        email2: $email2
        telephone: $telephone
        fax: $fax
        region: $region
        notInUse: $notInUse
        isHeadOffice: $isHeadOffice
        noLetter: $noLetter
        noVoucher: $noVoucher
        extrasChildPrice: $extrasChildPrice
        purchaseNominalCode: $purchaseNominalCode
        address: $address
        notes: $notes
        cancellationPolicy: $cancellationPolicy
        headOfficeId: $headOfficeId
        customFields: $customFields
        paymentTerms: $paymentTerms
        pickup: $pickup
        dropoff: $dropoff
        zone: $zone
        tags: $tags
        marketPlace: $marketPlace
        autoApprove: $autoApprove
        paymentMethod: $paymentMethod
        payeeName: $payeeName
        accountsEmail: $accountsEmail
        propagateMarketPlace: $propagateMarketPlace
      }
    ) {
      id
      serviceType
      name
      typeName
      altName
      code
      address
      email
      website
      geolocation
      languageId
      rating
      currencyCode
      defaultRateTypeCode
      boardBasis
      accountCode
      nominalCode
      purchaseNominalCode
      email2
      telephone
      fax
      region
      notInUse
      noLetter
      noVoucher
      marketPlace
      autoApprove
      address
      notes
      cancellationPolicy
      headOfficeId
      pickup
      dropoff
      zone
      tags
      payeeName
      accountsEmail
      paymentMethod
      extras {
        edges {
          node {
            id
            agentGroupName
            commission
            cost
            currencyCode
            discount
            groupId
            markup
            name
            percentPrice
            rateTypeCode
            sell
            isChildOnly
            percentChild
            isInfantOnly
            service {
              id
              name
              code
              typeName
              minOccupancy
              maxOccupancy
              stdOccupancy
            }
            typeId
            validFrom
            validTo
          }
        }
      }
      paymentTerms {
        edges {
          node {
            isPercent
            days
            amount
            altAmount
            termType
          }
        }
      }
      destination {
        id
        name
      }
      services {
        totalCount
        edges {
          node {
            id
            name
            code
            typeName
            minOccupancy
            maxOccupancy
            stdOccupancy
            notInUse
            noReport
          }
        }
      }
      customFields {
        totalCount
        edges {
          node {
            content
            name
            owner
            typeId
          }
        }
      }
    }
  }
`;
export const SUPPLIER_PRICES_CREATE_MUTATION = gql`
  mutation createSupplierPrices($prices: [PriceInput!]!) {
    createSupplierPrices(prices: $prices) {
      id
    }
  }
`;
export const SUPPLIER_ADD_EXTRAS_MUTATION = gql`
  mutation supplierAddExtras($supplierId: ID!, $extras: [SupplierAddExtrasItemInput]!) {
    supplierAddExtras(supplierId: $supplierId, extras: $extras) {
      success
      error
    }
  }
`;
export const EXTRA_UPDATE_MUTATION = gql`
  mutation extraUpdate($id: ID!, $extraInput: ExtraInput) {
    updateExtra(id: $id, extraInput: $extraInput) {
      id
    }
  }
`;
export const GENERATE_PURCHASE_INVOICES_MUTATION = gql`
  mutation generatePurchaseInvoices($itineraryId: ID!, $suppliers: [String!]!) {
    generatePurchaseInvoices(itineraryId: $itineraryId, suppliers: $suppliers) {
      id
      amount
    }
  }
`;
export const GENERATE_REPORT_MUTATION = gql`
  mutation generateReport($reportId: ID!, $filters: [ReportFilterInput]!) {
    task: generateReport(reportId: $reportId, filters: $filters) {
      id
      status
    }
  }
`;
export const PURCHASE_INVOICE_DELETE_MUTATION = gql`
  mutation purchaseInvoiceDelete($id: ID!) {
    deletePurchaseInvoice(id: $id) {
      id
    }
  }
`;
export const PURCHASE_INVOICE_PAYMENT_DELETE_MUTATION = gql`
  mutation purchaseInvoicePaymentDelete($id: ID!) {
    invoicePaymentMutation: deletePurchaseInvoicePayment(id: $id) {
      id
      amount
    }
  }
`;
export const SUPPLIER_CREATE_MUTATION = gql`
  mutation supplierCreate(
    $name: String
    $typeName: String
    $altName: String
    $code: String
    $email: String
    $website: String
    $geolocation: String
    $destinationId: ID
    $languageId: ID
    $currencyCode: String
    $rating: String
    $defaultRateTypeCode: String
    $boardBasis: String
    $accountCode: String
    $nominalCode: String
    $purchaseNominalCode: String
    $email2: String
    $telephone: String
    $fax: String
    $region: String
    $notInUse: Boolean
    $isHeadOffice: Boolean
    $marketPlace: Boolean
    $noLetter: Boolean
    $noVoucher: Boolean
    $extrasChildPrice: Boolean
    $address: String
    $notes: String
    $pickup: String
    $dropoff: String
    $serviceType: GtServiceType
    $cancellationPolicy: String
    $headOfficeId: ID
    $tags: String
    $zone: String
  ) {
    createSupplier(
      supplierInput: {
        name: $name
        typeName: $typeName
        altName: $altName
        code: $code
        email: $email
        website: $website
        geolocation: $geolocation
        destinationId: $destinationId
        languageId: $languageId
        rating: $rating
        currencyCode: $currencyCode
        defaultRateTypeCode: $defaultRateTypeCode
        boardBasis: $boardBasis
        accountCode: $accountCode
        nominalCode: $nominalCode
        email2: $email2
        telephone: $telephone
        fax: $fax
        region: $region
        notInUse: $notInUse
        isHeadOffice: $isHeadOffice
        marketPlace: $marketPlace
        noLetter: $noLetter
        noVoucher: $noVoucher
        extrasChildPrice: $extrasChildPrice
        purchaseNominalCode: $purchaseNominalCode
        address: $address
        notes: $notes
        cancellationPolicy: $cancellationPolicy
        headOfficeId: $headOfficeId
        serviceType: $serviceType
        pickup: $pickup
        dropoff: $dropoff
        tags: $tags
        zone: $zone
      }
    ) {
      id
      name
      code
      email
      geolocation
      marketPlace
      tags
      destination {
        id
        name
      }
    }
  }
`;
export const SUPPLIER_DELETE_MUTATION = gql`
  mutation supplierDelete($id: ID!) {
    deleteSupplier(id: $id) {
      id
    }
  }
`;
export const ALL_DESTINATIONS_SEARCH_QUERY = gql`
  query allDestinations($after: String, $filter: DestinationFilterType) {
    destinations(
      first: 10000
      after: $after
      filter: $filter
      orderBy: { fieldName: "name", direction: ASC }
    ) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          isRoot
          name
          code
          geolocation
          isDefault
          brand
          notInUse
          externalCode
          path
        }
      }
    }
  }
`;
export const DESTINATIONS_SEARCH_QUERY = gql`
  query destinationsSearch($after: String, $filter: DestinationFilterType, $first: Int) {
    destinations(
      first: $first
      after: $after
      filter: $filter
      orderBy: { fieldName: "name", direction: ASC }
    ) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          name
          code
          geolocation
          isDefault
          isRoot
          path
          brand
          notInUse
          externalCode
        }
      }
    }
  }
`;
export const DESTINATION_UPDATE_MUTATION = gql`
  mutation destinationUpdate(
    $id: ID!
    $name: String!
    $code: String
    $geolocation: String!
    $isDefault: Boolean
    $brand: String
    $notInUse: Boolean
    $externalCode: String
  ) {
    updateDestination(
      id: $id
      destinationInput: {
        name: $name
        code: $code
        geolocation: $geolocation
        isDefault: $isDefault
        brand: $brand
        notInUse: $notInUse
        externalCode: $externalCode
      }
    ) {
      id
      name
      code
      geolocation
      isDefault
      isRoot
      brand
      notInUse
      externalCode
      path
    }
  }
`;
export const DESTINATION_CREATE_MUTATION = gql`
  mutation destinationCreate(
    $parentId: ID!
    $name: String!
    $code: String
    $geolocation: String!
    $isDefault: Boolean
    $brand: String
    $notInUse: Boolean
    $externalCode: String
  ) {
    createDestination(
      parentId: $parentId
      destinationInput: {
        name: $name
        code: $code
        geolocation: $geolocation
        isDefault: $isDefault
        brand: $brand
        notInUse: $notInUse
        externalCode: $externalCode
      }
    ) {
      id
      name
      code
      geolocation
      isDefault
      isRoot
      brand
      notInUse
      externalCode
      path
    }
  }
`;
export const DESTINATION_DELETE_MUTATION = gql`
  mutation destinationDelete($id: ID!) {
    deleteDestination(id: $id) {
      id
      name
      code
      geolocation
      isDefault
    }
  }
`;
export const ALL_PICKUP_POINTS_SEARCH_QUERY = gql`
  query allPickupPointsSearch($after: String, $filter: PickupPointFilterType, $first: Int) {
    pickupPoints(first: $first, after: $after, filter: $filter) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          isDropoff
          isPickup
          name
          zone
          address
          coordinates
        }
      }
    }
  }
`;
export const PICKUP_CREATE_MUTATION = gql`
  mutation createPickupPoint($input: PickupPointInput) {
    createPickupPoint(pickuppointInput: $input) {
      id
      name
      zone
      isPickup
      isDropoff
    }
  }
`;
export const PICKUP_UPDATE_MUTATION = gql`
  mutation updatePickupPoint($id: ID!, $input: PickupPointInput) {
    updatePickupPoint(id: $id, pickuppointInput: $input) {
      id
      name
      zone
      isPickup
      isDropoff
    }
  }
`;
export const PICKUP_DELETE_MUTATION = gql`
  mutation pickupDelete($pickupId: ID!) {
    deletePickupPoint(id: $pickupId) {
      id
    }
  }
`;
export const ALL_LANGUAGES_SEARCH_QUERY = gql`
  query allLanguagesSearch {
    languages(orderBy: { fieldName: "name", direction: ASC }) {
      edges {
        node {
          id
          name
          code
          forContent
          isDefault
        }
      }
    }
  }
`;
export const LANGUAGE_CREATE_MUTATION = gql`
  mutation languageCreate(
    $name: String!
    $code: String!
    $forContent: Boolean!
    $isDefault: Boolean!
  ) {
    createLanguage(
      languageInput: {
        name: $name
        code: $code
        forContent: $forContent
        isDefault: $isDefault
      }
    ) {
      id
      name
      code
      forContent
      isDefault
    }
  }
`;
export const LANGUAGE_UPDATE_MUTATION = gql`
  mutation languageUpdate(
    $id: ID!
    $name: String!
    $code: String!
    $forContent: Boolean!
    $isDefault: Boolean!
  ) {
    updateLanguage(
      id: $id
      languageInput: {
        name: $name
        code: $code
        forContent: $forContent
        isDefault: $isDefault
      }
    ) {
      id
      name
      code
      forContent
      isDefault
    }
  }
`;
export const LANGUAGE_DELETE_MUTATION = gql`
  mutation languageDelete($languageId: ID!) {
    deleteLanguage(id: $languageId) {
      id
    }
  }
`;
export const ALL_TRANSLATIONS_SEARCH_QUERY = gql`
  query allTranslationsSearch {
    translations {
      edges {
        node {
          id
          label
          language {
            id
          }
          translation
        }
      }
    }
  }
`;
export const TRANSLATION_CREATE_MUTATION = gql`
  mutation translationCreate(
    $name: String
    $label: String!
    $translation: String
    $languageId: ID!
  ) {
    createTranslation(
      translationInput: {
        name: $name
        label: $label
        translation: $translation
        languageId: $languageId
      }
    ) {
      id
    }
  }
`;
export const TRANSLATION_UPDATE_MUTATION = gql`
  mutation translationUpdate($id: ID!, $label: String!, $translation: String, $languageId: ID!) {
    updateTranslation(
      id: $id
      translationInput: {
        label: $label
        translation: $translation
        languageId: $languageId
      }
    ) {
      id
    }
  }
`;
export const TRANSLATION_DELETE_MUTATION = gql`
  mutation translationDelete($translationId: ID!) {
    deleteTranslation(id: $translationId) {
      id
    }
  }
`;
export const ALL_HEAD_OFFICES_SEARCH_QUERY = gql`
  query allHeadOfficesSearch($filter: HeadOfficeFilterType) {
    headOffices(
      first: 10000
      filter: $filter
      orderBy: { fieldName: "name", direction: ASC }
    ) {
      edges {
        node {
          id
          name
          notes
          telephone
          address
          code
          email
          notInUse
          externalCode
        }
      }
    }
  }
`;
export const HEAD_OFFICES_WITH_SUPPLIERS = gql`
  query headOfficesWithSuppliers {
    headOffices(
      first: 10000
      filter: { not_in_use_eq: false }
      orderBy: { fieldName: "name", direction: ASC }
    ) {
      edges {
        node {
          id
          name
          notes
          telephone
          address
          code
          email
        }
      }
    }
    suppliers(filter: { is_head_office_eq: true }) {
      edges {
        node {
          id
          name
          notes
          telephone
          address
          code
          email
        }
      }
    }
  }
`;
export const HEAD_OFFICE_CREATE_MUTATION = gql`
  mutation headOfficeCreate(
    $name: String!
    $notes: String
    $telephone: String
    $address: String
    $code: String!
    $email: String!
    $notInUse: Boolean
    $externalCode: String
  ) {
    createHeadOffice(
      headofficeInput: {
        name: $name
        notes: $notes
        telephone: $telephone
        address: $address
        code: $code
        email: $email
        notInUse: $notInUse
        externalCode: $externalCode
      }
    ) {
      id
      name
      notes
      telephone
      address
      code
      email
      notInUse
      externalCode
    }
  }
`;
export const HEAD_OFFICE_UPDATE_MUTATION = gql`
  mutation headOfficeUpdate(
    $id: ID!
    $name: String!
    $notes: String
    $telephone: String
    $address: String
    $code: String!
    $email: String!
    $notInUse: Boolean
    $externalCode: String
  ) {
    updateHeadOffice(
      id: $id
      headofficeInput: {
        name: $name
        notes: $notes
        telephone: $telephone
        address: $address
        code: $code
        email: $email
        notInUse: $notInUse
        externalCode: $externalCode
      }
    ) {
      id
      name
      notes
      telephone
      address
      code
      email
      notInUse
      externalCode
    }
  }
`;
export const HEAD_OFFICE_DELETE_MUTATION = gql`
  mutation headOfficeDelete($headOfficeId: ID!) {
    deleteHeadOffice(id: $headOfficeId) {
      id
    }
  }
`;

export const ALL_STARS_SEARCH_QUERY = gql`
  query allStarsSearch {
    stars(first: 100) {
      edges {
        node {
          id
          name
          number
          notInUse
        }
      }
    }
  }
`;

export const ALL_CURRENCIES_SEARCH_QUERY = gql`
  query allCurrenciesSearch($includeExchangeRate: Boolean) {
    currencies(first: 200, includeExchangeRate: $includeExchangeRate) {
      edges {
        node {
          id
          name
          code
          symbol
          exchangeRate
        }
      }
    }
  }
`;
export const CURRENCY_CREATE_MUTATION = gql`
  mutation currencyCreate($name: String!, $code: String!, $symbol: String) {
    createCurrency(
      currencyInput: { name: $name, code: $code, symbol: $symbol }
    ) {
      id
      name
      symbol
      code
    }
  }
`;
export const CURRENCY_UPDATE_MUTATION = gql`
  mutation currencyUpdate($currencyId: ID!, $name: String!, $code: String!, $symbol: String) {
    updateCurrency(
      id: $currencyId
      currencyInput: { name: $name, code: $code, symbol: $symbol }
    ) {
      id
      name
      symbol
      code
    }
  }
`;
export const CURRENCY_DELETE_MUTATION = gql`
  mutation currencyDelete($currencyId: ID!) {
    deleteCurrency(id: $currencyId) {
      id
    }
  }
`;
export const ALL_EXCHANGE_RATES_SEARCH_QUERY = gql`
  query allExchangeRates($after: String) {
    exchangeRates(first: 250, after: $after) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          currencyCode
          dateFrom
          dateTo
          id
          rate
        }
      }
    }
  }
`;
export const EXCHANGE_RATE_CREATE_MUTATION = gql`
  mutation exchangeRateCreate($input: ExchangeRateInput) {
    createExchangeRate(exchangerateInput: $input) {
      id
      currencyCode
      dateFrom
      dateTo
      rate
    }
  }
`;
export const EXCHANGE_RATE_UPDATE_MUTATION = gql`
  mutation exchangeRateUpdate($id: ID!, $input: ExchangeRateInput) {
    updateExchangeRate(id: $id, exchangerateInput: $input) {
      id
      currencyCode
      dateFrom
      dateTo
      rate
    }
  }
`;
export const EXCHANGE_RATE_DELETE_MUTATION = gql`
  mutation exchangeRateDelete($id: ID!) {
    deleteExchangeRate(id: $id) {
      id
      currencyCode
      dateFrom
      dateTo
      rate
    }
  }
`;
export const ALL_RATE_TYPES_SEARCH_QUERY = gql`
  query allRateTypesSearch {
    rateTypes(first: 1000) {
      totalCount
      edges {
        node {
          id
          name
          code
          includePax
          includeDays
          includeQuantity
          minPax
          maxPax
          minStay
          maxStay
          notInUse
        }
      }
    }
  }
`;
export const RATE_TYPE_CREATE_MUTATION = gql`
  mutation rateTypeCreate($input: RateTypeInput) {
    createRateType(ratetypeInput: $input) {
      id
      name
      code
      includePax
      includeDays
      includeQuantity
      maxPax
      minPax
      minStay
      maxStay
      notInUse
    }
  }
`;
export const RATE_TYPE_UPDATE_MUTATION = gql`
  mutation rateTypeUpdate($id: ID!, $input: RateTypeInput) {
    updateRateType(id: $id, ratetypeInput: $input) {
      id
      name
      code
      includePax
      includeDays
      includeQuantity
      minPax
      maxPax
      minStay
      maxStay
      notInUse
    }
  }
`;
export const RATE_TYPE_DELETE_MUTATION = gql`
  mutation rateTypeDelete($rateId: ID!) {
    deleteRateType(id: $rateId) {
      id
    }
  }
`;
export const ALL_BOARD_BASES_SEARCH_QUERY = gql`
  query allBoardBasesSearch {
    boardBases {
      totalCount
      edges {
        node {
          id
          name
          notInUse
        }
      }
    }
  }
`;
export const SERVICE_STATUSES_LIST = gql`
  query serviceStatusesList {
    serviceStatuses {
      totalCount
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;

export const ALL_SERVICE_TYPES_QUERY = gql`
  query allServiceTypes($supplierType: GtServiceType!) {
    serviceTypes(supplierType: $supplierType) {
      totalCount
      edges {
        node {
          id
          name
          code
          minOccupancy
          maxOccupancy
          stdOccupancy
          notInUse
          hidden
        }
      }
    }
  }
`;
export const SERVICE_UPDATE_MUTATION = gql`
  mutation updateService(
    $id: ID!
    $name: String
    $altName: String
    $code: String
    $typeName: String
    $minOccupancy: String
    $maxOccupancy: String
    $stdOccupancy: String
    $notInUse: Boolean
    $marketPlace: Boolean
    $noReport: Boolean
    $noVoucher: Boolean
    $skipMarkup: Boolean
    $skipCircuitDiscount: Boolean
    $skipAgentCommission: Boolean
    $taxCode: String
  ) {
    updateService(
      id: $id
      serviceInput: {
        name: $name
        altName: $altName
        code: $code
        typeName: $typeName
        minOccupancy: $minOccupancy
        maxOccupancy: $maxOccupancy
        stdOccupancy: $stdOccupancy
        notInUse: $notInUse
        noReport: $noReport
        skipMarkup: $skipMarkup
        skipCircuitDiscount: $skipCircuitDiscount
        skipAgentCommission: $skipAgentCommission
        taxCode: $taxCode
        marketPlace: $marketPlace
        noVoucher: $noVoucher
      }
    ) {
      id
      name
      altName
      code
      typeName
      minOccupancy
      maxOccupancy
      stdOccupancy
      notInUse
      noReport
      skipMarkup
      skipCircuitDiscount
      skipAgentCommission
      taxCode
      marketPlace
      noVoucher
    }
  }
`;
export const SERVICE_CREATE_MUTATION = gql`
  mutation createService(
    $supplierId: ID!
    $name: String
    $altName: String
    $code: String
    $typeName: String
    $minOccupancy: String
    $maxOccupancy: String
    $stdOccupancy: String
    $notInUse: Boolean
    $noReport: Boolean
    $skipMarkup: Boolean
    $skipCircuitDiscount: Boolean
    $skipAgentCommission: Boolean
    $taxCode: String
    $marketPlace: Boolean
    $noVoucher: Boolean
  ) {
    createService(
      supplierId: $supplierId
      serviceInput: {
        name: $name
        altName: $altName
        code: $code
        typeName: $typeName
        minOccupancy: $minOccupancy
        maxOccupancy: $maxOccupancy
        stdOccupancy: $stdOccupancy
        notInUse: $notInUse
        noReport: $noReport
        skipMarkup: $skipMarkup
        skipCircuitDiscount: $skipCircuitDiscount
        skipAgentCommission: $skipAgentCommission
        taxCode: $taxCode
        marketPlace: $marketPlace
        noVoucher: $noVoucher
      }
    ) {
      id
      name
      altName
      code
      typeName
      minOccupancy
      maxOccupancy
      stdOccupancy
      notInUse
      noReport
      skipMarkup
      skipCircuitDiscount
      skipAgentCommission
      taxCode
      marketPlace
      noVoucher
    }
  }
`;
export const SERVICE_DELETE_MUTATION = gql`
  mutation serviceDelete($serviceId: ID!) {
    deleteService(id: $serviceId) {
      id
    }
  }
`;
export const ALL_SERVICE_PRICES_QUERY = gql`
  query allServicePrices($serviceId: ID!) {
    servicePrices(serviceId: $serviceId) {
      totalCount
      edges {
        node {
          id
          dateFrom
          dateTo
          rateTypeCode
          currencyCodeBuy
          currencyCodeSell
          adultBuy1
          adultSell1
          groupId
        }
      }
    }
  }
`;
export const SERVICE_PRICE_RECORD_QUERY = gql`
  query servicePriceDetails($id: ID!) {
    servicePrice(id: $id) {
      adultBuy1
      adultBuy2
      adultBuy3
      adultBuy4
      adultBuy5
      adultBuy6
      adultBuy7
      adultSell1
      adultSell2
      adultSell3
      adultSell4
      adultSell5
      adultSell6
      adultSell7
      agentGroup {
        id
        name
      }
      childBuy1
      childBuy2
      childBuy3
      childBuy4
      childBuy5
      childBuy6
      childBuy7
      childSell1
      childSell2
      childSell3
      childSell4
      childSell5
      childSell6
      childSell7
      currencyCode
      dateFrom
      dateTo
      groupId
      id
      infantBuy1
      infantBuy2
      infantBuy3
      infantBuy4
      infantBuy5
      infantBuy6
      infantBuy7
      infantSell1
      infantSell2
      infantSell3
      infantSell4
      infantSell5
      infantSell6
      infantSell7
      maxStay
      minStay
      maxPax
      minPax
      markup
      ratePlan
      remarks
      rateTypeCode
      exception
      marketPlace
      api
      businessModel
      supplierCommission
      service {
        id
        name
      }
    }
  }
`;
export const SERVICE_PRICE_UPDATE_MUTATION = gql`
  mutation updateServicePriceMutation(
    $id: ID!
    $dateFrom: DateTimeType
    $dateTo: DateTimeType
    $rateTypeCode: String
    $ratePlan: String
    # $currencyCodeBuy: String
    # $currencyCodeSell: String
    $currencyCode: String
    $adultBuy1: Float
    $adultSell1: Float
    $adultBuy2: Float
    $adultSell2: Float
    $adultBuy3: Float
    $adultSell3: Float
    $adultBuy4: Float
    $adultSell4: Float
    $adultBuy5: Float
    $adultSell5: Float
    $adultBuy6: Float
    $adultSell6: Float
    $adultBuy7: Float
    $adultSell7: Float
    $childBuy1: Float
    $childSell1: Float
    $childBuy2: Float
    $childSell2: Float
    $childBuy3: Float
    $childSell3: Float
    $childBuy4: Float
    $childSell4: Float
    $childBuy5: Float
    $childSell5: Float
    $childBuy6: Float
    $childSell6: Float
    $childBuy7: Float
    $childSell7: Float
    $infantBuy1: Float
    $infantSell1: Float
    $infantBuy2: Float
    $infantSell2: Float
    $infantBuy3: Float
    $infantSell3: Float
    $infantBuy4: Float
    $infantSell4: Float
    $infantBuy5: Float
    $infantSell5: Float
    $infantBuy6: Float
    $infantSell6: Float
    $infantBuy7: Float
    $infantSell7: Float
    $minStay: Int
    $maxStay: Int
    $minPax: Int
    $maxPax: Int
    $markup: Float
    $groupId: ID
    $serviceId: ID
    $exception: Boolean
    $marketPlace: Boolean
    $api: Boolean
    $businessModel: String
    $supplierCommission: Float
    $remarks: String
  ) {
    updateServicePrice(
      id: $id
      priceInput: {
        dateFrom: $dateFrom
        dateTo: $dateTo
        rateTypeCode: $rateTypeCode
        ratePlan: $ratePlan
        # currencyCodeBuy: $currencyCodeBuy
        # currencyCodeSell: $currencyCodeSell
        currencyCode: $currencyCode
        adultBuy1: $adultBuy1
        adultSell1: $adultSell1
        adultBuy2: $adultBuy2
        adultSell2: $adultSell2
        adultBuy3: $adultBuy3
        adultSell3: $adultSell3
        adultBuy4: $adultBuy4
        adultSell4: $adultSell4
        adultBuy5: $adultBuy5
        adultSell5: $adultSell5
        adultBuy6: $adultBuy6
        adultSell6: $adultSell6
        adultBuy7: $adultBuy7
        adultSell7: $adultSell7
        childBuy1: $childBuy1
        childSell1: $childSell1
        childBuy2: $childBuy2
        childSell2: $childSell2
        childBuy3: $childBuy3
        childSell3: $childSell3
        childBuy4: $childBuy4
        childSell4: $childSell4
        childBuy5: $childBuy5
        childSell5: $childSell5
        childBuy6: $childBuy6
        childSell6: $childSell6
        childBuy7: $childBuy7
        childSell7: $childSell7
        infantBuy1: $infantBuy1
        infantSell1: $infantSell1
        infantBuy2: $infantBuy2
        infantSell2: $infantSell2
        infantBuy3: $infantBuy3
        infantSell3: $infantSell3
        infantBuy4: $infantBuy4
        infantSell4: $infantSell4
        infantBuy5: $infantBuy5
        infantSell5: $infantSell5
        infantBuy6: $infantBuy6
        infantSell6: $infantSell6
        infantBuy7: $infantBuy7
        infantSell7: $infantSell7
        minStay: $minStay
        maxStay: $maxStay
        minPax: $minPax
        maxPax: $maxPax
        markup: $markup
        groupId: $groupId
        serviceId: $serviceId
        exception: $exception
        marketPlace: $marketPlace
        api: $api
        businessModel: $businessModel
        supplierCommission: $supplierCommission
        remarks: $remarks
      }
    ) {
      id
      rateTypeCode
      dateFrom
      dateTo
      rateTypeCode
      ratePlan
      currencyCode
      adultBuy1
      adultSell1
      adultBuy2
      adultSell2
      adultBuy3
      adultSell3
      adultBuy4
      adultSell4
      adultBuy5
      adultSell5
      adultBuy6
      adultSell6
      adultBuy7
      adultSell7
      childBuy1
      childSell1
      childBuy2
      childSell2
      childBuy3
      childSell3
      childBuy4
      childSell4
      childBuy5
      childSell5
      childBuy6
      childSell6
      childBuy7
      childSell7
      infantBuy1
      infantSell1
      infantBuy2
      infantSell2
      infantBuy3
      infantSell3
      infantBuy4
      infantSell4
      infantBuy5
      infantSell5
      infantBuy6
      infantSell6
      infantBuy7
      infantSell7
      minStay
      maxStay
      markup
      groupId
      exception
      marketPlace
      remarks
      api
      supplierCommission
      businessModel
      service {
        id
      }
      agentGroup {
        id
        name
      }
    }
  }
`;
export const SERVICE_PRICE_CREATE_MUTATION = gql`
  mutation servicePriceCreate(
    $serviceId: ID!
    $dateFrom: DateTimeType
    $dateTo: DateTimeType
    $rateTypeCode: String
    $currencyCodeBuy: String
    $currencyCodeSell: String
    $adultBuy1: Float
    $adultSell1: Float
  ) {
    createServicePrice(
      serviceId: $serviceId
      priceInput: {
        dateFrom: $dateFrom
        dateTo: $dateTo
        rateTypeCode: $rateTypeCode
        currencyCodeBuy: $currencyCodeBuy
        currencyCodeSell: $currencyCodeSell
        adultBuy1: $adultBuy1
        adultSell1: $adultSell1
      }
    ) {
      id
      rateTypeCode
      dateFrom
      dateTo
      rateTypeCode
      ratePlan
      currencyCodeBuy
      currencyCodeSell
      adultBuy1
      adultSell1
    }
  }
`;
export const SERVICE_PRICE_DELETE_MUTATION = gql`
  mutation servicePriceDelete($priceId: ID!) {
    deleteServicePrice(id: $priceId) {
      id
    }
  }
`;
export const ALL_AGENTS_BASIC_LIST = gql`
  query allActiveAgents($filter: AgentFilterType) {
    agents(
      first: 5000
      orderBy: { fieldName: "name", direction: ASC }
      filter: $filter
    ) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;
export const ALL_AGENTS_WITH_CURRENCY = gql`
  query agentsWithCurrency($filter: AgentFilterType) {
    agents(
      first: 1000
      orderBy: { fieldName: "companyCode", direction: ASC } #http://tracker.globetrack.ie/issues/6027 - had to sort by company code as sita has thousand of agents and Sita admin otherwhise is not showing by default on the list
      filter: $filter
    ) {
      edges {
        node {
          id
          name
          companyCode
          department
          isOwner
          financeSettings {
            currencyCode
          }
        }
      }
    }
  }
`;
export const BRANDS_QUERY = gql`
  query brandsList($after: String, $filter: BrandFilterType) {
    brands(first: 10000, after: $after, filter: $filter) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;
export const LEAD_SOURCES_QUERY = gql`
  query leadSourcesList($after: String, $filter: LeadSourceFilterType) {
    leadSources(first: 10000, after: $after, filter: $filter) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;
export const ALL_DEPARTMENTS_BASIC_LIST = gql`
  query allDepartmentsBasicList {
    departments(first: 10000, filter: { not_in_use_eq: false }) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;
export const UPDATE_AVAILABLE_DOC = gql`
  mutation updateAvailDoc($input: AvailableDocumentInput!) {
    updateAvailableDocument(input: $input) {
      id
      name
      customFields {
        totalCount
        edges {
          node {
            content
            name
            typeId
          }
        }
      }
    }
  }
`;
export const AI_STYLE_DOCUMENT = gql`
  mutation aiStyleDocument($input: AiStyleDocumentInput!) {
    aiStyleDocument(input: $input) {
      reply
      css
      success
      error
    }
  }
`;

export const AVAILABLE_DOCUMENTS_LIST_WITH_CF = gql`
  query docTypesWithCustomFields {
    availableDocuments {
      edges {
        node {
          id
          name
          docId
          customFields {
            totalCount
            edges {
              node {
                content
                name
                typeId
              }
            }
          }
        }
      }
    }
  }
`;

export const AVAILABLE_DOCUMENTS_LIST = gql`
  query availableDocumentsList {
    availableDocuments {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;
export const ITINERARY_CREATE_MUTATION = gql`
  mutation itineraryCreate(
    $isLocked: Boolean
    $adults: String!
    $children: String!
    $infants: String!
    $startDate: DateTimeType!
    $endDate: DateTimeType!
    $destinationId: ID!
    $agentId: ID!
    $department: String!
    $internalStatus: String
    $brand: String
    $internalReference: String
    $items: [ItineraryItemInput]
    $participants: [ItineraryParticipantInput!]!
    $bookedById: ID
    $clientReference: String
    $notes: String
    $comps: [ItineraryCompInput]
    $copyFromId: ID
    $logs: [ItineraryLogInput]
    $priceCheck: Boolean
    $leadSourceIds: [ID]
  ) {
    createItinerary(
      itineraryInput: {
        isLocked: $isLocked
        adults: $adults
        children: $children
        infants: $infants
        startDate: $startDate
        endDate: $endDate
        destinationId: $destinationId
        agentId: $agentId
        internalStatus: $internalStatus
        internalReference: $internalReference
        department: $department
        clientReference: $clientReference
        items: $items
        participants: $participants
        bookedById: $bookedById
        brand: $brand
        notes: $notes
        comps: $comps
        copyFromId: $copyFromId
        logs: $logs
        priceCheck: $priceCheck
        leadSourceIds: $leadSourceIds
      }
    ) {
      id
      reference
    }
  }
`;
export const ITINERARY_RECALCULATE_MUTATION = gql`
  mutation reacalculateItQuery(
    $adults: String!
    $children: String!
    $infants: String!
    $startDate: DateTimeType!
    $endDate: DateTimeType!
    $destinationId: ID!
    $agentId: ID!
    $department: String!
    $participants: [ItineraryParticipantInput!]!
    $items: [ItineraryItemInput]
    $force: Boolean
    $priceCheck: Boolean
    $id: ID
  ) {
    recalculateItinerary(
      force: $force
      id: $id
      itineraryInput: {
        adults: $adults
        children: $children
        infants: $infants
        startDate: $startDate
        endDate: $endDate
        destinationId: $destinationId
        agentId: $agentId
        department: $department
        participants: $participants
        items: $items
        priceCheck: $priceCheck
      }
    ) {
      profit {
        commission
        commissionAdjustment
        netCommission
        netProfit
        grossProfit
        totalCost
        totalSell
        margin
        markup
        tomsTax
      }
      agent {
        id
        name
      }
      sourceItinerary {
        id
        reference
      }
      items {
        edges {
          node {
            id
            orderNo
            parentId
            service {
              id
              stdOccupancy
              altName
              typeName
            }
            supplier {
              id
            }
            supplierName
            serviceName
            serviceTypeName
            startDate
            endDate
            lastUpdated
            totalPrice
            totalCostInBase
            totalSellInBase
            rateTypeCode
            status
            statusId
            qty
            isOptional
            isIncluded
            isHidden
            isMandatory
            includeCommission
            tags
            taxCode
            fromDeparture
            openGroupDepartureItemId
            hasApprovedPurchaseInvoice
            hasPurchaseInvoice
            boardBasis
            supplierComp
            passComp
            pax {
              adults
              children
              infants
            }
            customPricing
            businessModel
            businessModelName
            supplierCommission
            supplierCommissionValue
            commission
            yearShift
            isHistoricalRate
            margin
            markup
            pricing {
              adultBuy
              adultSell
              childBuy
              childSell
              infantBuy
              infantSell
              itemBuy
              itemSell
              infantSell
              rateTypeCode
              ratePlan
              currencyBuy
              currencyBuyExchangeRate
              currencySell
              currencySellExchangeRate
              itemBased
              dayBased
              discountCost
              discountSell
              discountInfo
              circuitDiscountCost
              circuitDiscountSell
              totalBuy

              pricingDays {
                allocation
                adultSell
                childSell
                itemSell
                discountPercent
                discountCost
                discountSell
              }
              logs {
                date
                username
                rateType
                itemBuy
                itemSell
                adultBuy
                adultSell
                childBuy
                childSell
                infantBuy
                infantSell
                currencyBuy
                currencySell
                currencyBuyExchangeRate
                currencySellExchangeRate
                supplierComp
                passComp
              }
              ${EXPLAINER_FIELDS}
            }
            source {
              name
              id
              sourceId
              confirmationRef
            }
            extras {
              id
              uid
              name
              adults
              children
              infants
              qty
              startDate
              endDate
              totalSell
              totalBuy
              currencySell
              currencyBuy
              isChildOnly
              percentChild
              isInfantOnly
              isMandatory
              rateBase
              rateTypeCode
              cost
              sell
              percentPrice
              customPricing
              currencySellExchangeRate
              currencyBuyExchangeRate
              isHidden
              isOptional
              isIncluded
              totalCostInBase
              totalSellInBase
              markup
              margin
              status
              statusId
              discount
              discountCost
              discountSell
              circuitDiscountCost
              circuitDiscountSell
              includeCommission
              commission
              orderNo
              ${EXPLAINER_FIELDS}
              remarks {
                pickupPoint
                dropoffPoint
                pickupTime
                dropoffTime
                timeFrom
                agentNotes
                supplierNotes
                voucherNotes
                flightNo
                confirmationNo
                roomNumbers
                internalNotes
              }
            }
            remarks {
              pickupPoint
              dropoffPoint
              pickupTime
              dropoffTime
              timeFrom
              agentNotes
              supplierNotes
              voucherNotes
              flightNo
              roomNumbers
            }
          }
        }
      }
    }
  }
`;
export const AVAILABLE_SERVICES_SEARCH_QUERY = gql`
  query availableServices(
    $destinationId: ID!
    $agentId: ID!
    $startDate: DateTimeType
    $endDate: DateTimeType
    $serviceType: GtServiceType
    $adults: String
    $children: String
    $infants: String
    $supplierName: String
    $department: String
    $serviceTypeNames: [String]
    $ratings: [String]
    $first: Int
  ) {
    availableServices(
      searchParams: {
        destinationId: $destinationId
        agentId: $agentId
        department: $department
        startDate: $startDate
        endDate: $endDate
        serviceType: $serviceType
        adults: $adults
        children: $children
        infants: $infants
        serviceTypeNames: $serviceTypeNames
        ratings: $ratings
      }
      filter: { supplierNameContains: $supplierName }
      first: $first
    ) {
      edges {
        node {
          id
          serviceName
          serviceId
          supplierName
          supplierId
          priceFrom
          priceFromCurrencyCode
          rateTypeCode
          supplier {
            code
            boardBasis
            imageURL
            description
          }
          service {
            stdOccupancy
            code
            imageURL
            description
          }
          prices {
            id
            date
            currency
            ratePlan
            rateCode
            rateTypeName
            adultPrice
            childPrice
            infantPrice
            itemPrice
            isClosed
            isOnRequest
            remaining
          }
        }
      }
    }
  }
`;
export const ITINERARY_RECORD_FOR_BUILDER = gql`
  query itiRecordForBuilder($id: ID!) {
    itinerary(id: $id) {
      timeSinceLastEdit
      createdAt
      editedByName
      editedByFingerprint
      profit {
        commission
        commissionAdjustment
        netCommission
        netProfit
        grossProfit
        totalCost
        totalSell
        margin
        markup
        tomsTax
      }
      id
      startDate
      endDate
      internalStatus
      bookingStatus
      internalReference
      clientReference
      reference
      department
      notes
      totalPrice
      currencyCode
      brand
      leadSource
      leadSources {
        id
        name
      }
      isLocked
      bookedBy {
        id
        name
      }
      agent {
        id
        name
        department
        financeSettings {
          currencyCode
        }
      }
      sourceItinerary {
        id
        reference
      }
      openGroupDeparture {
        id
        name
        startDate
        openGroup {
          id
          name
        }
      }
      destination {
        id
        name
      }
      pax {
        adults
        children
        infants
      }
      comps {
        edges {
          node {
            id
            pass
            free
            paxFrom
            paxTo
            supplierCompId
            supplierId
            serviceId
            service {
              id
              name
            }
            supplier {
              id
              name
            }
          }
        }
      }
      serviceGroups {
        edges {
          node {
            id
            name
            description
            participants {
              edges {
                node {
                  id
                  title
                  name
                  surname
                }
              }
            }
            items {
              edges {
                node {
                  id
                  itineraryItemId
                  adults
                  children
                  infants
                }
              }
            }
          }
        }
      }
      participants {
        edges {
          node {
            title
            name
            surname
            mobile
            telephone
            email
            rooms
            groupLabel
            client {
              id
            }
          }
        }
      }
      items {
        edges {
          node {
            id
            orderNo
            service {
              id
              stdOccupancy
              altName
            }
            supplier {
              id
              headOffice {
                id
                name
              }
            }
            supplierName
            supplierDestination
            serviceName
            serviceTypeName
            startDate
            endDate
            lastUpdated
            totalPrice
            totalCostInBase
            totalSellInBase
            toms
            margin
            markup
            rateTypeCode
            status
            statusId
            parentId
            qty
            isOptional
            isIncluded
            isHidden
            isMandatory
            includeCommission
            tags
            taxCode
            fromDeparture
            openGroupDepartureItemId
            hasApprovedPurchaseInvoice
            hasPurchaseInvoice
            boardBasis
            pax {
              adults
              children
              infants
            }
            customPricing
            businessModel
            businessModelName
            supplierCommission
            supplierCommissionValue
            commission
            supplierComp
            passComp
            yearShift
            isHistoricalRate
            pricing {
              adultBuy
              adultSell
              childBuy
              childSell
              infantBuy
              infantSell
              itemBuy
              itemSell
              infantSell
              rateTypeCode
              ratePlan
              currencyBuy
              currencyBuyExchangeRate
              currencySell
              currencySellExchangeRate
              itemBased
              dayBased
              discountCost
              discountSell
              discountInfo
              circuitDiscountCost
              circuitDiscountSell
              discountDescription
              totalBuy

              pricingDays {
                allocation
                adultSell
                childSell
                itemSell
                discountPercent
                discountCost
                discountSell
              }
              logs {
                date
                time
                username
                rateType
                adultBuy
                adultSell
                childBuy
                childSell
                infantBuy
                infantSell
                itemBuy
                itemSell
                currencyBuy
                currencySell
                currencyBuyExchangeRate
                currencySellExchangeRate
                supplierComp
                supplierCommission
                passComp
              }
              ${EXPLAINER_FIELDS}
            }
            source {
              name
              id
              sourceId
              confirmationRef
              rsResultServiceId
            }
            extras {
              id
              uid
              name
              adults
              children
              infants
              qty
              startDate
              endDate
              totalSell
              totalBuy
              currencySell
              currencyBuy
              isChildOnly
              percentChild
              isInfantOnly
              isMandatory
              rateBase
              rateTypeCode
              cost
              sell
              percentPrice
              customPricing
              isHidden
              isOptional
              isIncluded
              status
              statusId
              commission
              includeCommission
              supplierCommission
              totalCostInBase
              totalSellInBase
              markup
              margin
              discount
              discountCost
              discountSell
              circuitDiscountCost
              circuitDiscountSell
              currencySellExchangeRate
              currencyBuyExchangeRate
              toms
              orderNo
              ${EXPLAINER_FIELDS}
              remarks {
                pickupPoint
                dropoffPoint
                pickupTime
                dropoffTime
                timeFrom
                agentNotes
                supplierNotes
                voucherNotes
                flightNo
                confirmationNo
                roomNumbers
                internalNotes
              }
            }
            remarks {
              pickupPoint
              dropoffPoint
              pickupTime
              dropoffTime
              timeFrom
              agentNotes
              supplierNotes
              voucherNotes
              flightNo
              confirmationNo
              roomNumbers
              internalNotes
            }
          }
        }
      }
    }
  }
`;
export const ITINERARY_UPDATE_MUTATION = gql`
  mutation itineraryUpdate(
    $isLocked: Boolean
    $id: ID!
    $recalculate: Boolean
    $adults: String!
    $children: String!
    $infants: String!
    $startDate: DateTimeType!
    $endDate: DateTimeType!
    $destinationId: ID!
    $agentId: ID!
    $department: String!
    $internalStatus: String
    $brand: String
    $leadSourceIds: [ID]
    $internalReference: String
    $clientReference: String
    $items: [ItineraryItemInput]
    $participants: [ItineraryParticipantInput!]!
    $serviceGroups: [ItineraryServiceGroupInput!]
    $notes: String
    $bookedById: ID
    $logs: [ItineraryLogInput]
    $comps: [ItineraryCompInput]
  ) {
    updateItinerary(
      id: $id
      recalculate: $recalculate
      itineraryInput: {
        isLocked: $isLocked
        adults: $adults
        children: $children
        infants: $infants
        startDate: $startDate
        endDate: $endDate
        destinationId: $destinationId
        agentId: $agentId
        internalStatus: $internalStatus
        brand: $brand
        leadSourceIds: $leadSourceIds
        internalReference: $internalReference
        clientReference: $clientReference
        department: $department
        items: $items
        participants: $participants
        notes: $notes
        bookedById: $bookedById
        serviceGroups: $serviceGroups
        logs: $logs
        comps: $comps
      }
    ) {
      error
      message
      itinerary {
        id
        reference
      }
    }
  }
`;
export const ITINERARY_DELETE_MUTATION = gql`
  mutation itineraryDelete($id: ID!) {
    deleteItinerary(id: $id) {
      id
    }
  }
`;
export const GENERATE_DOC_MUTATION = gql`
  mutation generateDoc($itineraryId: ID!, $documentTypeId: ID!) {
    generateDocument(
      itineraryId: $itineraryId
      documentTypeId: $documentTypeId
    ) {
      id
      status
    }
  }
`;
export const GENERATE_MHIKE_EXPORT = gql`
  mutation generateMhikeExport($itineraryId: ID!) {
    exportItineraryToMhikes(id: $itineraryId) {
      success
      error
      message
    }
  }
`;
export const GENERATE_SAFARI_EXPORT = gql`
  mutation generateSafariExport($itineraryId: ID!) {
    exportItineraryToSafari(id: $itineraryId) {
      success
      error
      message
    }
  }
`;
export const GENERATE_WETU_EXPORT = gql`
  mutation generateWetuExport($itineraryId: ID!) {
    exportItineraryToWetu(id: $itineraryId) {
      success
      error
      message
    }
  }
`;
export const GENERATE_TRAVEFY_EXPORT = gql`
  mutation generateTravefyExport($itineraryId: ID!) {
    exportItineraryToTravefy(id: $itineraryId) {
      success
      error
      message
    }
  }
`;
export const GENERATE_AXUS_EXPORT = gql`
  mutation generateAxusExport($itineraryId: ID!) {
    exportItineraryToAxus(id: $itineraryId) {
      success
      error
      message
    }
  }
`;
export const GENERATE_SP_CALENDAR_EXPORT = gql`
  mutation generateSpCalendarExport($itineraryId: ID!) {
    exportItineraryToSPCalendar(id: $itineraryId) {
      success
      error
      message
    }
  }
`;
export const GENERATE_LUGGAGE_CSV = gql`
  mutation generateLuggageCsv($itineraryId: ID!) {
    generateLuggageCSV(id: $itineraryId) {
      success
      error
      message
    }
  }
`;
// #10962 Luggage Transfers — shared node fragment
const LUGGAGE_TRANSFER_FIELDS = `
  id
  itineraryId
  itineraryRef
  legKey
  date
  pickupLocation
  pickupAddress
  pickupTel
  dropoffLocation
  dropoffAddress
  dropoffTel
  assignedParticipants
  bags
  included
  gapWarning
`;
// Itinerary tab read
export const ITINERARY_LUGGAGE_TRANSFERS = gql`
  query itineraryLuggageTransfers($id: ID!) {
    itinerary(id: $id) {
      id
      luggageTransfers {
        ${LUGGAGE_TRANSFER_FIELDS}
      }
    }
  }
`;
// Plugin list (connection + filters + paging)
export const LUGGAGE_TRANSFERS = gql`
  query luggageTransfers($filter: LuggageTransferFilterType, $first: Int, $after: String) {
    luggageTransfers(filter: $filter, first: $first, after: $after) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          ${LUGGAGE_TRANSFER_FIELDS}
        }
      }
    }
  }
`;
export const UPDATE_LUGGAGE_TRANSFER = gql`
  mutation updateLuggageTransfer(
    $id: ID!
    $bags: Int
    $included: Boolean
    $assignedParticipants: [String]
    $applyToAll: Boolean
  ) {
    updateLuggageTransfer(
      id: $id
      bags: $bags
      included: $included
      assignedParticipants: $assignedParticipants
      applyToAll: $applyToAll
    ) {
      success
      error
      message
    }
  }
`;
export const GENERATE_LUGGAGE_TRANSFERS_CSV = gql`
  mutation generateLuggageTransfersCsv($itineraryId: ID!) {
    generateLuggageTransfersCsv(id: $itineraryId) {
      success
      error
      message
    }
  }
`;
export const EXPORT_LUGGAGE_TRANSFERS = gql`
  mutation exportLuggageTransfers($filter: LuggageTransferFilterType, $ids: [ID]) {
    exportLuggageTransfers(filter: $filter, ids: $ids) {
      success
      error
      message
    }
  }
`;
export const GENERATE_SALES_INVOICES = gql`
  mutation generateSalesInvoices($itineraryId: ID!) {
    generateSalesInvoice(itineraryId: $itineraryId) {
      id
    }
  }
`;
export const SALES_INVOICE_DELETE_MUTATION = gql`
  mutation salesInvoiceDelete($id: ID!) {
    deleteSalesInvoice(id: $id) {
      id
    }
  }
`;
export const EXPORT_INVOICE_TO_XERO_MUTATION = gql`
  mutation exportToXero($input: XeroExportInvoiceInput!) {
    exportInvoiceToXero(input: $input) {
      status
      message
    }
  }
`;
export const EXPORT_INVOICE_TO_QUICK_BOOKS_MUTATION = gql`
  mutation exportToQuickBooks($id: ID!) {
    exportInvoiceToQuickBooks(id: $id) {
      success
      error
      message
    }
  }
`;
export const EXPORT_PAYMENT_TO_QUICK_BOOKS_MUTATION = gql`
  mutation exportToQuickBooks($id: ID!) {
    exportPaymentToQuickBooks(id: $id) {
      success
      error
      message
    }
  }
`;
export const EXPORT_PAYMENT_TO_XERO_MUTATION = gql`
  mutation exportToXero($input: XeroExportPaymentInput!) {
    exportPaymentToXero(input: $input) {
      status
      message
    }
  }
`;
export const TASK_STATUS_QUERY = gql`
  query taskStatus($taskId: ID!) {
    taskStatus(id: $taskId) {
      status
      notes
    }
  }
`;
export const TASK_STATUS_BY_NAME_QUERY = gql`
  query taskStatusByName($name: String!) {
    taskStatus(name: $name) {
      status
      notes
    }
  }
`;
export const SIGNED_URL_QUERY = gql`
  query signedUrl($fileName: String!) {
    signedUrl(fileName: $fileName) {
      url
    }
  }
`;

export const CREATE_ITINERARY_ATTACHMENT = gql`
  mutation createItineraryAttachment($input: ItineraryAttachmentInput!) {
    createItineraryAttachment(input: $input) {
      status
    }
  }
`;

export const CREATE_RATES_IMPORT_ATTACHMENT = gql`
  mutation createRatesImportAttachment($input: RatesImportAttachmentInput!) {
    createRatesImportAttachment(input: $input) {
      status
    }
  }
`;

export const UPDATE_ITINERARY_DOC = gql`
  mutation updateItiDoc($input: ItineraryDocumentInput!) {
    updateItineraryDocument(input: $input) {
      id
      name
      createdAt
      createdBy {
        name
      }
      downloadURL
      editURL
      updatedAt
      forApp
    }
  }
`;
export const ITINERARY_DOCS_WITH_AVAILABLE = gql`
  query itiDocsWithAvailableDocs($itineraryId: ID!) {
    itineraryDocuments(itineraryId: $itineraryId) {
      edges {
        node {
          id
          isAttachment
          name
          createdAt
          createdBy {
            name
          }
          downloadURL
          editURL
          updatedAt
          forApp
          documentType
          publishedDocuments {
            edges {
              node {
                id
                url
                createdAt
                publishedByName
                format
              }
            }
          }
        }
      }
    }
    availableDocuments {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;

export const CONTENT_UPDATE_MUTATION = gql`
  mutation contentUpdate(
    $id: ID!
    $name: String!
    $description: String
    $imgURL: String
    $languageId: ID
    $forWeb: Boolean
    $forReports: Boolean
    $forQuotation: Boolean
    $forConfirmation: Boolean
    $forVoucher: Boolean
    $forOther: Boolean
    $forApp: Boolean
    $isDefault: Boolean
    $sequence: Int
    $images: [ImageInput]
  ) {
    updateContent(
      id: $id
      contentInput: {
        name: $name
        description: $description
        imgURL: $imgURL
        languageId: $languageId
        forWeb: $forWeb
        forReports: $forReports
        forQuotation: $forQuotation
        forConfirmation: $forConfirmation
        forVoucher: $forVoucher
        forOther: $forOther
        forApp: $forApp
        isDefault: $isDefault
        sequence: $sequence
        images: $images
      }
    ) {
      id
      name
      description
      owner
      forWeb
      forReports
      forQuotation
      forConfirmation
      forVoucher
      forOther
      forApp
      isDefault
      sequence
    }
  }
`;
export const CONTENT_CREATE_MUTATION = gql`
  mutation contentCreate(
    $name: String!
    $description: String
    $imgURL: String
    $languageId: ID
    $ownerId: ID!
    $owner: String
    $forWeb: Boolean
    $forReports: Boolean
    $forQuotation: Boolean
    $forConfirmation: Boolean
    $forVoucher: Boolean
    $forOther: Boolean
    $forApp: Boolean
    $isDefault: Boolean
    $sequence: Int
    $images: [ImageInput]
  ) {
    createContent(
      ownerId: $ownerId
      contentInput: {
        name: $name
        description: $description
        imgURL: $imgURL
        languageId: $languageId
        owner: $owner
        forWeb: $forWeb
        forReports: $forReports
        forQuotation: $forQuotation
        forConfirmation: $forConfirmation
        forVoucher: $forVoucher
        forOther: $forOther
        forApp: $forApp
        isDefault: $isDefault
        sequence: $sequence
        images: $images
      }
    ) {
      id
      name
      description
      owner
      forWeb
      forReports
      forQuotation
      forConfirmation
      forVoucher
      forOther
      isDefault
      sequence
    }
  }
`;
export const CONTENTS_SEARCH_QUERY = gql`
  query contentsSearch($first: Int, $filter: ContentFilterType) {
    contents(first: $first, filter: $filter) {
      edges {
        node {
          id
          description
          imgURL
        }
      }
    }
  }
`;
export const SERVICES_SEARCH_QUERY = gql`
  query servicesForTemplate(
    $filter: ServiceFilterType
    $first: Int
    $extraFilter: ExtraFilterType
  ) {
    services(first: $first, filter: $filter) {
      edges {
        node {
          id
          name
          stdOccupancy
          maxOccupancy

          extras(filter: $extraFilter) {
            edges {
              node {
                id
                name
                typeId
                isMandatory
              }
            }
          }
          linkedServices {
            edges {
              node {
                id
                name
                stdOccupancy
                maxOccupancy
                supplier {
                  id
                  name
                  code
                  serviceType
                  notInUse
                }
              }
            }
          }
          supplier {
            id
            name
            code
            serviceType
            notInUse
          }
        }
      }
    }
  }
`;
export const TEMPLATE_CREATE_MUTATION = gql`
  mutation templateCreate($input: TemplateInput!) {
    createTemplate(templateInput: $input) {
      id
    }
  }
`;
export const QUICK_ADD_TEMPLATES_SEARCH_QUERY = gql`
  query quickAddTemplatesSearch($after: String) {
    templates(first: 5000, after: $after) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          name
          description
          destination {
            id
            name
          }
          department
          duration
          items {
            edges {
              node {
                id
                day
                duration
                service {
                  id
                  name
                }
                supplier {
                  id
                  name
                  serviceType
                }
              }
            }
          }
        }
      }
    }
  }
`;
export const ALL_TEMPLATES_SEARCH_QUERY = gql`
  query allTemplatesSearch(
    $after: String
    $filter: TemplateFilterType
    $sort: SortOrderType
    $includeExternal: Boolean
  ) {
    templates(
      first: 1000
      after: $after
      filter: $filter
      orderBy: $sort
      includeExternal: $includeExternal
    ) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          name
          description
          isExternal
          destination {
            id
            name
          }
          department
          duration
        }
      }
    }
  }
`;
export const TEMPLATE_RECORD_QUERY = gql`
  query templateRecord($id: ID!) {
    template(id: $id) {
      id
      name
      destination {
        id
        name
      }
      department
      duration
      description
      brand
      internalStatus
      marketPlace
      templateType {
        id
      }
      departures {
        edges {
          node {
            id
            validFrom
            validTo
            daysOfWeek
          }
        }
      }
      slabs {
        edges {
          node {
            id
            name
            description
            paxFrom
            foc
            items {
              edges {
                node {
                  id
                  templateItemUid
                  adults
                  infants
                  children
                  foc
                  passComp
                  supplierComp
                  qty
                }
              }
            }
          }
        }
      }
      items {
        edges {
          node {
            id
            uid
            parentUid
            service {
              id
              name
              stdOccupancy
              maxOccupancy
            }
            supplier {
              id
              name
              serviceType
            }
            day
            order
            duration
            itemBuy
            itemSell
            adultBuy
            adultSell
            childBuy
            childSell
            currencyBuy
            currencySell
            rateTypeCode
            totalSell
            totalBuy
            customPricing
            includeMarkup
            hidden
            optional
            included
            extras {
              edges {
                node {
                  id
                  extraId
                  name
                  day
                  duration
                  included
                  optional
                }
              }
            }
          }
        }
      }
    }
  }
`;
export const TEMPLATE_DETAILS_QUERY = gql`
  query templateDetails(
    $id: ID!
    $agentId: ID
    $department: String
    $adults: Int
    $children: Int
    $infants: Int
    $isExternal: Boolean
    $pricingDate: DateTimeType
  ) {
    templateDetails: template(
      id: $id
      agentId: $agentId
      department: $department
      adults: $adults
      children: $children
      infants: $infants
      isExternal: $isExternal
      pricingDate: $pricingDate
    ) {
      id
      name
      destination {
        id
        name
      }
      department
      duration
      description
      brand
      internalStatus
      isExternal
      departures {
        edges {
          node {
            id
            daysOfWeek
            validFrom
            validTo
          }
        }
      }
      items {
        edges {
          node {
            id
            uid
            parentUid
            service {
              id
              name
              stdOccupancy
              typeName
              serviceNotes(
                filter: { department: $department, agentId: $agentId }
              ) {
                edges {
                  node {
                    id
                    dateFrom
                    dateTo
                    description
                    department
                  }
                }
              }
            }
            supplier {
              id
              name
              serviceType
              boardBasis
              defaultRateTypeCode
            }
            day
            order
            duration
            itemBuy
            itemSell
            adultBuy
            adultSell
            childBuy
            childSell
            infantBuy
            infantSell
            currencyBuy
            currencySell
            rateTypeCode
            totalSell
            totalBuy
            customPricing
            hidden
            optional
            included
            extras {
              edges {
                node {
                  id
                  extraId
                  name
                  day
                  duration
                  isChildOnly
                  isInfantOnly
                  included
                  optional
                }
              }
            }
          }
        }
      }
    }
  }
`;
export const TEMPLATE_ITEM_UPDATE_MUTATION = gql`
  mutation templateItemUpdate($input: TemplateItemInput, $id: ID!) {
    updateTemplateItem(id: $id, templateItemInput: $input) {
      id
    }
  }
`;
export const TEMPLATE_UPDATE_MUTATION = gql`
  mutation templateUpdate($id: ID!, $input: TemplateInput!) {
    updateTemplate(id: $id, templateInput: $input) {
      id
    }
  }
`;
export const CLIENT_RECORD_QUERY = gql`
  query clientRecord($id: ID!) {
    client(id: $id) {
      id
      title
      name
      surname
      email
      telephone
      mobile
      birthDate
      address
      notes
      nationality
      language
      code
      state
      postCode
      city
      clientTypeId
      clientStatusId
      customFields {
        totalCount
        edges {
          node {
            content
            name
            owner
            typeId
          }
        }
      }
    }
  }
`;
export const PAYMENT_RECORD_QUERY = gql`
  query paymentRecord($id: ID!) {
    payment(id: $id) {
      agentName
      amount
      createdByName
      currencyCode
      department
      exchangeRate
      id
      itineraryReference
      itineraryStartDate
      lodgementDate
      lodgementReference
      notes
      payerName
      payerSurname
      type
    }
  }
`;
export const PAYMENT_UPDATE_MUTATION = gql`
  mutation paymentUpdate(
    $id: ID!
    $payerName: String
    $payerSurname: String
    $department: String
    $notes: String
    $type: String
    $lodgementReference: String
    $lodgementDate: DateTimeType
    $amount: Float!
    $exchangeRate: Float
    $currencyCode: String
    $exported: Boolean
    $paymentMethodId: ID
  ) {
    updatePayment(
      id: $id
      paymentInput: {
        payerName: $payerName
        payerSurname: $payerSurname
        department: $department
        notes: $notes
        type: $type
        lodgementReference: $lodgementReference
        lodgementDate: $lodgementDate
        amount: $amount
        exchangeRate: $exchangeRate
        currencyCode: $currencyCode
        exported: $exported
        paymentMethodId: $paymentMethodId
      }
    ) {
      id
      payerName
      payerSurname
      department
      notes
      type
      lodgementReference
      lodgementDate
      amount
      exchangeRate
      currencyCode
      exported
      paymentMethodId
    }
  }
`;
export const PAYMENT_CREATE_MUTATION = gql`
  mutation createPayment(
    $payerName: String
    $payerSurname: String
    $department: String
    $itineraryId: ID!
    $notes: String
    $type: String
    $lodgementReference: String
    $lodgementDate: DateTimeType
    $amount: Float!
    $exchangeRate: Float
    $currencyCode: String
    $paymentMethodId: ID
  ) {
    createPayment(
      itineraryId: $itineraryId
      paymentInput: {
        payerName: $payerName
        payerSurname: $payerSurname
        department: $department
        notes: $notes
        type: $type
        lodgementReference: $lodgementReference
        lodgementDate: $lodgementDate
        amount: $amount
        exchangeRate: $exchangeRate
        currencyCode: $currencyCode
        paymentMethodId: $paymentMethodId
      }
    ) {
      id
    }
  }
`;
export const PAYMENT_DELETE_MUTATION = gql`
  mutation paymentDelete($id: ID!) {
    deletePayment(id: $id) {
      id
    }
  }
`;

export const CLIENT_UPDATE_MUTATION = gql`
  mutation clientUpdate(
    $id: ID!
    $title: String
    $name: String
    $surname: String
    $email: String
    $mobile: String
    $telephone: String
    $code: String
    $birthDate: DateTimeType
    $address: String
    $notes: String
    $nationality: String
    $language: String
    $state: String
    $postCode: String
    $city: String
    $customFields: [CustomFieldInput]
    $clientTypeId: ID
    $clientStatusId: ID
  ) {
    updateClient(
      id: $id
      clientInput: {
        title: $title
        name: $name
        surname: $surname
        email: $email
        mobile: $mobile
        telephone: $telephone
        code: $code
        address: $address
        notes: $notes
        birthDate: $birthDate
        language: $language
        nationality: $nationality
        state: $state
        postCode: $postCode
        city: $city
        customFields: $customFields
        clientTypeId: $clientTypeId
        clientStatusId: $clientStatusId
      }
    ) {
      id
      title
      name
      surname
      email
      telephone
      mobile
      birthDate
      address
      notes
      nationality
      language
      code
      state
      postCode
      city
      clientTypeId
      clientStatusId
      customFields {
        totalCount
        edges {
          node {
            content
            name
            owner
            typeId
          }
        }
      }
    }
  }
`;
export const CLIENT_CREATE_MUTATION = gql`
  mutation clientCreate(
    $title: String
    $name: String
    $surname: String
    $email: String
    $mobile: String
    $telephone: String
    $code: String
    $birthDate: DateTimeType
    $address: String
    $notes: String
    $nationality: String
    $language: String
    $state: String
    $postCode: String
    $city: String
    $clientTypeId: ID
    $clientStatusId: ID
  ) {
    createClient(
      clientInput: {
        title: $title
        name: $name
        surname: $surname
        email: $email
        mobile: $mobile
        telephone: $telephone
        code: $code
        address: $address
        notes: $notes
        birthDate: $birthDate
        language: $language
        nationality: $nationality
        state: $state
        postCode: $postCode
        city: $city
        clientTypeId: $clientTypeId
        clientStatusId: $clientStatusId
      }
    ) {
      id
    }
  }
`;
export const CLIENT_DELETE_MUTATION = gql`
  mutation clientDelete($id: ID!) {
    deleteClient(id: $id) {
      id
    }
  }
`;

export const FLIGHTS_QUERY = gql`
  query flightsList {
    flights {
      edges {
        node {
          id
          validFrom
          validTo
          flightNo
          weekDays
        }
      }
    }
  }
`;

export const TAXES_QUERY = gql`
  query taxesList {
    taxes {
      edges {
        node {
          description
          code
          id
          isAccommodation
          isActivity
          isTransport
          isTravel
          isOther
          isTransfer
          percentage
          validFrom
          validTo
        }
      }
    }
  }
`;
export const TAX_UPDATE_MUTATION = gql`
  mutation taxUpdate($taxId: ID!, $input: TaxInput) {
    updateTax(id: $taxId, taxInput: $input) {
      id
      description
      code
      percentage
      isAccommodation
      isActivity
      isTransport
      isTravel
      isOther
      isTransfer
      validFrom
      validTo
    }
  }
`;
export const TAX_CREATE_MUTATION = gql`
  mutation taxCreate($input: TaxInput) {
    createTax(taxInput: $input) {
      id
      description
      code
      percentage
      isAccommodation
      isActivity
      isTransport
      isTravel
      isOther
      isTransfer
    }
  }
`;
export const TAX_DELETE_MUTATION = gql`
  mutation taxDelete($taxId: ID!) {
    deleteTax(id: $taxId) {
      id
    }
  }
`;
export const AGENT_RECORD_QUERY = gql`
  query agentRecord($id: ID!) {
    agent(id: $id) {
      id
      isOwner
      name
      email
      address
      telephone
      languageId
      department
      companyCode
      agentType
      image
      city
      region
      state
      postCode
      notInUse
      notes
      roundDiscounts
      roundMarkups
      externalDomain
      accessSettings {
        groupName
        settingName
        settingValue
      }
      financeSettings {
        accommodationCommision
        accommodationMarkup
        accountCode
        accountsDepartmentCode
        activitiesCommision
        activitiesMarkup
        currencyCode
        otherCommision
        otherMarkup
        packageCommision
        packageMarkup
        taxType
        transportCommision
        transportMarkup
        travelCommision
        travelMarkup
        chargeCommission
      }
      configSettings {
        dateFormat
        quotationMaxAdults
        quotationMaxChildren
        quotationMaxDays
        quotationMaxInfants
        quotationStartDays
        sendItineraryNotification
      }
      paymentTerms {
        edges {
          node {
            isPercent
            days
            amount
            altAmount
            termType
          }
        }
      }
      customFields {
        totalCount
        edges {
          node {
            content
            name
            owner
            typeId
          }
        }
      }
    }
  }
`;
export const STAFF_RECORD_QUERY = gql`
  query staffDetails($id: ID!) {
    user(id: $id) {
      id
      name
      email
      telephone
      department
      username
      typeId
      destinationId
      brand
      isActive
      notInUse
      isSupervisor
      emailVerified
      notes
      accessSettings {
        groupName
        settingName
        settingValue
      }
      config {
        sendItineraryNotification
      }
      customFields {
        totalCount
        edges {
          node {
            content
            name
            typeId
          }
        }
      }
    }
  }
`;
export const ALL_ACCOMMODATION_SERVICE_TYPES_SEARCH_QUERY = gql`
  query allAccommodationServiceTypesSearch {
    accommodationServiceTypes {
      edges {
        node {
          id
          code
          maxOccupancy
          minOccupancy
          name
          purchaseNominalCode
          salesNominalCode
          stdOccupancy
          taxCode
          notInUse
        }
      }
    }
  }
`;
export const ACCOMMODATION_SERVICE_TYPE_CREATE_MUTATION = gql`
  mutation accommodationServiceTypeCreate(
    $name: String
    $code: String
    $stdOccupancy: Int
    $minOccupancy: Int
    $maxOccupancy: Int
    $salesNominalCode: String
    $purchaseNominalCode: String
    $taxCode: String
    $notInUse: Boolean
  ) {
    createAccommodationServiceType(
      accommodationservicetypeInput: {
        name: $name
        code: $code
        stdOccupancy: $stdOccupancy
        minOccupancy: $minOccupancy
        maxOccupancy: $maxOccupancy
        salesNominalCode: $salesNominalCode
        purchaseNominalCode: $purchaseNominalCode
        taxCode: $taxCode
        notInUse: $notInUse
      }
    ) {
      id
      name
      code
      stdOccupancy
      minOccupancy
      maxOccupancy
      salesNominalCode
      purchaseNominalCode
      taxCode
      notInUse
    }
  }
`;
export const ACCOMMODATION_SERVICE_TYPE_UPDATE_MUTATION = gql`
  mutation accommodationServiceTypeUpdate(
    $id: ID!
    $name: String
    $code: String
    $stdOccupancy: Int
    $minOccupancy: Int
    $maxOccupancy: Int
    $salesNominalCode: String
    $purchaseNominalCode: String
    $taxCode: String
    $notInUse: Boolean
  ) {
    updateAccommodationServiceType(
      id: $id
      accommodationservicetypeInput: {
        name: $name
        code: $code
        stdOccupancy: $stdOccupancy
        minOccupancy: $minOccupancy
        maxOccupancy: $maxOccupancy
        salesNominalCode: $salesNominalCode
        purchaseNominalCode: $purchaseNominalCode
        taxCode: $taxCode
        notInUse: $notInUse
      }
    ) {
      id
      name
      code
      stdOccupancy
      minOccupancy
      maxOccupancy
      salesNominalCode
      purchaseNominalCode
      taxCode
      notInUse
    }
  }
`;
export const ACCOMMODATION_SERVICE_TYPE_DELETE_MUTATION = gql`
  mutation accommodationServiceTypeDelete($accommodationServiceTypeId: ID!) {
    deleteAccommodationServiceType(id: $accommodationServiceTypeId) {
      id
    }
  }
`;
export const ALL_ACTIVITY_SERVICE_TYPES_SEARCH_QUERY = gql`
  query allActivityServiceTypesSearch {
    activityServiceTypes {
      edges {
        node {
          id
          name
          purchaseNominalCode
          salesNominalCode
          taxCode
          stdOccupancy
          minOccupancy
          maxOccupancy
          notInUse
        }
      }
    }
  }
`;
export const ACTIVITY_SERVICE_TYPE_CREATE_MUTATION = gql`
  mutation activityServiceTypeCreate($input: ActivityServiceTypeInput) {
    createActivityServiceType(input: $input) {
      id
      name
      salesNominalCode
      purchaseNominalCode
      taxCode
      notInUse
    }
  }
`;
export const ACTIVITY_SERVICE_TYPE_UPDATE_MUTATION = gql`
  mutation activityServiceTypeUpdate($id: ID!, $input: ActivityServiceTypeInput) {
    updateActivityServiceType(id: $id, input: $input) {
      id
      name
      salesNominalCode
      purchaseNominalCode
      taxCode
      stdOccupancy
      minOccupancy
      maxOccupancy
      notInUse
    }
  }
`;
export const ACTIVITY_SERVICE_TYPE_DELETE_MUTATION = gql`
  mutation activityServiceTypeDelete($id: ID!) {
    deleteActivityServiceType(id: $id) {
      id
    }
  }
`;
export const ALL_TRANSPORT_SERVICE_TYPES_SEARCH_QUERY = gql`
  query allTransportServiceTypesSearch {
    transportServiceTypes {
      edges {
        node {
          id
          name
          purchaseNominalCode
          salesNominalCode
          taxCode
          stdOccupancy
          minOccupancy
          maxOccupancy
          notInUse
        }
      }
    }
  }
`;
export const TRANSPORT_SERVICE_TYPE_CREATE_MUTATION = gql`
  mutation createTransportServiceType($input: TransportServiceTypeInput) {
    createTransportServiceType(input: $input) {
      id
      name
      salesNominalCode
      purchaseNominalCode
      taxCode
      stdOccupancy
      minOccupancy
      maxOccupancy
      notInUse
    }
  }
`;
export const TRANSPORT_SERVICE_TYPE_UPDATE_MUTATION = gql`
  mutation transportServiceTypeUpdate($id: ID!, $input: TransportServiceTypeInput) {
    updateTransportServiceType(id: $id, input: $input) {
      id
      name
      salesNominalCode
      purchaseNominalCode
      taxCode
      stdOccupancy
      minOccupancy
      maxOccupancy
      notInUse
    }
  }
`;
export const TRANSPORT_SERVICE_TYPE_DELETE_MUTATION = gql`
  mutation transportServiceTypeDelete($id: ID!) {
    deleteTransportServiceType(id: $id) {
      id
    }
  }
`;
export const ALL_TRAVEL_SERVICE_TYPES_SEARCH_QUERY = gql`
  query allTravelServiceTypesSearch {
    travelServiceTypes {
      edges {
        node {
          id
          name
          purchaseNominalCode
          salesNominalCode
          taxCode
          stdOccupancy
          minOccupancy
          maxOccupancy
          notInUse
        }
      }
    }
  }
`;
export const TRAVEL_SERVICE_TYPE_CREATE_MUTATION = gql`
  mutation createTravelType($input: TravelServiceTypeInput) {
    createTravelServiceType(input: $input) {
      id
      name
      salesNominalCode
      purchaseNominalCode
      taxCode
      stdOccupancy
      minOccupancy
      maxOccupancy
      notInUse
    }
  }
`;
export const TRAVEL_SERVICE_TYPE_UPDATE_MUTATION = gql`
  mutation travelServiceTypeUpdate($id: ID!, $input: TravelServiceTypeInput) {
    updateTravelServiceType(id: $id, input: $input) {
      id
      name
      salesNominalCode
      purchaseNominalCode
      taxCode
      stdOccupancy
      minOccupancy
      maxOccupancy
      notInUse
    }
  }
`;
export const TRAVEL_SERVICE_TYPE_DELETE_MUTATION = gql`
  mutation travelServiceTypeDelete($id: ID!) {
    deleteTravelServiceType(id: $id) {
      id
    }
  }
`;
export const ALL_PACKAGE_SERVICE_TYPES_SEARCH_QUERY = gql`
  query allPackageServiceTypesSearch {
    packageServiceTypes {
      edges {
        node {
          id
          name
          purchaseNominalCode
          salesNominalCode
          taxCode
          hidden
          stdOccupancy
          minOccupancy
          maxOccupancy
          notInUse
        }
      }
    }
  }
`;
export const ALL_OTHER_SERVICE_TYPES_SEARCH_QUERY = gql`
  query allOtherServiceTypesSearch {
    otherServiceTypes {
      edges {
        node {
          id
          name
          purchaseNominalCode
          salesNominalCode
          taxCode
          hidden
          stdOccupancy
          minOccupancy
          maxOccupancy
          notInUse
        }
      }
    }
  }
`;
export const PACKAGE_SERVICE_TYPE_CREATE_MUTATION = gql`
  mutation createPackageServiceType($input: PackageServiceTypeInput) {
    createPackageServiceType(input: $input) {
      id
      name
      salesNominalCode
      purchaseNominalCode
      taxCode
      hidden
      stdOccupancy
      minOccupancy
      maxOccupancy
      notInUse
    }
  }
`;
export const OTHER_SERVICE_TYPE_CREATE_MUTATION = gql`
  mutation createOtherServiceType($input: OtherServiceTypeInput) {
    createOtherServiceType(input: $input) {
      id
      name
      salesNominalCode
      purchaseNominalCode
      taxCode
      hidden
      stdOccupancy
      minOccupancy
      maxOccupancy
      notInUse
    }
  }
`;
export const PACKAGE_SERVICE_TYPE_UPDATE_MUTATION = gql`
  mutation updatePackageServiceType($id: ID!, $input: PackageServiceTypeInput) {
    updatePackageServiceType(id: $id, input: $input) {
      id
      name
      salesNominalCode
      purchaseNominalCode
      taxCode
      hidden
      stdOccupancy
      minOccupancy
      maxOccupancy
      notInUse
    }
  }
`;
export const OTHER_SERVICE_TYPE_UPDATE_MUTATION = gql`
  mutation updateOtherServiceType($id: ID!, $input: OtherServiceTypeInput) {
    updateOtherServiceType(id: $id, input: $input) {
      id
      name
      salesNominalCode
      purchaseNominalCode
      taxCode
      hidden
      stdOccupancy
      minOccupancy
      maxOccupancy
      notInUse
    }
  }
`;
export const PACKAGE_SERVICE_TYPE_DELETE_MUTATION = gql`
  mutation packageServiceTypeDelete($id: ID!) {
    deletePackageServiceType(id: $id) {
      id
    }
  }
`;
export const OTHER_SERVICE_TYPE_DELETE_MUTATION = gql`
  mutation otherServiceTypeDelete($id: ID!) {
    deleteOtherServiceType(id: $id) {
      id
    }
  }
`;
export const ALL_EXTRA_SERVICE_TYPES_SEARCH_QUERY = gql`
  query extraTypes($filter: ExtraTypeFilterType) {
    extraTypes(filter: $filter) {
      edges {
        node {
          id
          name
          code
          taxCode
          capacityChange
          rateTypeCode
          isAccommodation
          isActivity
          isTransport
          isTravel
          isOther
          isPackage
          isChildOnly
          percentChild
          isInfantOnly
          commission
          markup
          discount
          isMandatory
          noReport
          taxCode
          notInUse
          noVoucher
        }
      }
    }
  }
`;
export const EXTRA_SERVICE_TYPE_CREATE_MUTATION = gql`
  mutation extraServiceTypeCreate(
    $name: String
    $code: String
    $taxCode: String
    $capacityChange: Int
    $rateTypeCode: String
    $isAccommodation: Boolean
    $isActivity: Boolean
    $isTransport: Boolean
    $isTravel: Boolean
    $isOther: Boolean
    $isPackage: Boolean
    $isChildOnly: Boolean
    $percentChild: Boolean
    $isInfantOnly: Boolean
    $isMandatory: Boolean
    $commission: Boolean
    $markup: Boolean
    $discount: Boolean
    $noReport: Boolean
    $notInUse: Boolean
    $noVoucher: Boolean
  ) {
    createExtraType(
      extratypeInput: {
        name: $name
        code: $code
        taxCode: $taxCode
        capacityChange: $capacityChange
        rateTypeCode: $rateTypeCode
        isAccommodation: $isAccommodation
        isActivity: $isActivity
        isTransport: $isTransport
        isTravel: $isTravel
        isOther: $isOther
        isPackage: $isPackage
        isChildOnly: $isChildOnly
        percentChild: $percentChild
        isInfantOnly: $isInfantOnly
        isMandatory: $isMandatory
        commission: $commission
        markup: $markup
        discount: $discount
        noReport: $noReport
        notInUse: $notInUse
        noVoucher: $noVoucher
      }
    ) {
      id
      name
      code
      taxCode
      capacityChange
      rateTypeCode
      isAccommodation
      isActivity
      isTransport
      isTravel
      isOther
      isPackage
      isChildOnly
      percentChild
      isInfantOnly
      isMandatory
      commission
      markup
      discount
      noReport
      notInUse
      noVoucher
    }
  }
`;
export const EXTRA_SERVICE_TYPE_UPDATE_MUTATION = gql`
  mutation extraServiceTypeUpdate(
    $id: ID!
    $name: String
    $code: String
    $taxCode: String
    $capacityChange: Int
    $rateTypeCode: String
    $isAccommodation: Boolean
    $isActivity: Boolean
    $isTransport: Boolean
    $isTravel: Boolean
    $isOther: Boolean
    $isPackage: Boolean
    $isChildOnly: Boolean
    $percentChild: Boolean
    $isInfantOnly: Boolean
    $isMandatory: Boolean
    $commission: Boolean
    $markup: Boolean
    $discount: Boolean
    $noReport: Boolean
    $notInUse: Boolean
    $noVoucher: Boolean
  ) {
    updateExtraType(
      id: $id
      extratypeInput: {
        name: $name
        code: $code
        taxCode: $taxCode
        capacityChange: $capacityChange
        rateTypeCode: $rateTypeCode
        isAccommodation: $isAccommodation
        isActivity: $isActivity
        isTransport: $isTransport
        isTravel: $isTravel
        isOther: $isOther
        isPackage: $isPackage
        isChildOnly: $isChildOnly
        percentChild: $percentChild
        isInfantOnly: $isInfantOnly
        commission: $commission
        markup: $markup
        discount: $discount
        isMandatory: $isMandatory
        noReport: $noReport
        notInUse: $notInUse
        noVoucher: $noVoucher
      }
    ) {
      id
      name
      code
      taxCode
      capacityChange
      rateTypeCode
      isAccommodation
      isActivity
      isTransport
      isTravel
      isOther
      isPackage
      isChildOnly
      percentChild
      isInfantOnly
      isMandatory
      commission
      markup
      discount
      noReport
      notInUse
      noVoucher
    }
  }
`;
export const EXTRA_SERVICE_TYPE_DELETE_MUTATION = gql`
  mutation extraServiceTypeDelete($extraServiceTypeId: ID!) {
    deleteExtraType(id: $extraServiceTypeId) {
      id
    }
  }
`;
export const EXTRA_DELETE_MUTATION = gql`
  mutation extraDelete($id: ID!) {
    deleteExtra(id: $id) {
      id
    }
  }
`;
export const ALL_ACCOMMODATION_SUPPLIER_TYPES_SEARCH_QUERY = gql`
  query allAccommodationSupplierTypesSearch {
    accommodationSupplierTypes {
      edges {
        node {
          id
          name
          notInUse
        }
      }
    }
  }
`;
export const ALL_ITINERARY_STATUSES_SEARCH_QUERY = gql`
  query itineraryStatuses {
    itineraryStatuses {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;
export const ACCOMMODATION_SUPPLIER_TYPE_CREATE_MUTATION = gql`
  mutation accommodationSupplierTypeCreate($name: String, $notInUse: Boolean) {
    createAccommodationSupplierType(
      accommodationsuppliertypeInput: { name: $name, notInUse: $notInUse }
    ) {
      id
      name
      notInUse
    }
  }
`;
export const ACCOMMODATION_SUPPLIER_TYPE_UPDATE_MUTATION = gql`
  mutation accommodationSupplierTypeUpdate($id: ID!, $name: String, $notInUse: Boolean) {
    updateAccommodationSupplierType(
      id: $id
      accommodationsuppliertypeInput: { name: $name, notInUse: $notInUse }
    ) {
      id
      name
      notInUse
    }
  }
`;
export const ACCOMMODATION_SUPPLIER_TYPE_DELETE_MUTATION = gql`
  mutation accommodationSupplierTypeDelete($accommodationSupplierId: ID!) {
    deleteAccommodationSupplierType(id: $accommodationSupplierId) {
      id
    }
  }
`;

export const ALL_BOARD_BASIS_TYPES_SEARCH_QUERY = gql`
  query allBoardBasisTypesSearch {
    boardBases {
      edges {
        node {
          id
          name
          code
          notInUse
        }
      }
    }
  }
`;
export const BOARD_BASIS_TYPE_CREATE_MUTATION = gql`
  mutation boardBasisTypeCreate($name: String, $code: String, $notInUse: Boolean) {
    createBoardBasis(
      boardbasisInput: { name: $name, code: $code, notInUse: $notInUse }
    ) {
      id
      name
      code
      notInUse
    }
  }
`;
export const BOARD_BASIS_TYPE_UPDATE_MUTATION = gql`
  mutation boardBasisTypeUpdate($id: ID!, $name: String, $code: String, $notInUse: Boolean) {
    updateBoardBasis(
      id: $id
      boardbasisInput: { name: $name, code: $code, notInUse: $notInUse }
    ) {
      id
      name
      code
      notInUse
    }
  }
`;
export const BOARD_BASIS_TYPE_DELETE_MUTATION = gql`
  mutation boardBasisTypeDelete($boardBasisId: ID!) {
    deleteBoardBasis(id: $boardBasisId) {
      id
    }
  }
`;
export const STAR_CREATE_MUTATION = gql`
  mutation starCreate($name: String, $number: Int, $notInUse: Boolean) {
    createStar(
      starsInput: { name: $name, number: $number, notInUse: $notInUse }
    ) {
      id
      name
      number
      notInUse
    }
  }
`;
export const STAR_UPDATE_MUTATION = gql`
  mutation starUpdate($id: ID!, $name: String, $number: Int, $notInUse: Boolean) {
    updateStar(
      id: $id
      starsInput: { name: $name, number: $number, notInUse: $notInUse }
    ) {
      id
      name
      number
      notInUse
    }
  }
`;
export const STAR_DELETE_MUTATION = gql`
  mutation starDelete($starId: ID!) {
    deleteStar(id: $starId) {
      id
    }
  }
`;
export const ALL_NOMINAL_CODES_SEARCH_QUERY = gql`
  query allNominalCodesSearch {
    nominalCodes {
      edges {
        node {
          id
          purchaseCode
          salesCode
          supplierType
        }
      }
    }
  }
`;
export const NOMINAL_CODE_UPDATE_MUTATION = gql`
  mutation nominalCodeUpdate($id: ID!, $purchaseCode: String, $salesCode: String) {
    updateNominalCode(
      id: $id
      nominalcodeInput: { purchaseCode: $purchaseCode, salesCode: $salesCode }
    ) {
      id
      purchaseCode
      salesCode
      supplierType
    }
  }
`;
export const ALL_CHARGE_TYPES_SEARCH_QUERY = gql`
  query allChargeTypesSearch {
    chargeTypes {
      edges {
        node {
          id
          description
          isAccommodation
          isActivity
          isOther
          isTransport
          isTravel
          nominalCode
          rateTypeCode
          taxCode
        }
      }
    }
  }
`;
export const CHARGE_TYPE_CREATE_MUTATION = gql`
  mutation chargeTypeCreate($input: ChargeInput) {
    createCharge(chargeInput: $input) {
      id
      description
      isAccommodation
      isActivity
      isOther
      isTransport
      isTravel
      nominalCode
      rateTypeCode
      taxCode
    }
  }
`;
export const CHARGE_TYPE_UPDATE_MUTATION = gql`
  mutation chargeTypeUpdate($id: ID!, $input: ChargeInput) {
    updateCharge(id: $id, chargeInput: $input) {
      id
      description
      isAccommodation
      isActivity
      isOther
      isTransport
      isTravel
      nominalCode
      rateTypeCode
      taxCode
    }
  }
`;
export const CHARGE_TYPE_DELETE_MUTATION = gql`
  mutation chargeTypeDelete($chargeTypeId: ID!) {
    deleteCharge(id: $chargeTypeId) {
      id
    }
  }
`;
export const ALL_DEPARTMENTS_SEARCH_QUERY = gql`
  query allDepartmentsSearch {
    departments {
      edges {
        node {
          id
          name
          taxType
          notInUse
          salesNominalCode
          purchaseNominalCode
          code
          paymentTerms {
            edges {
              node {
                amount
                altAmount
                days
                isPercent
                termType
              }
            }
          }
        }
      }
    }
  }
`;
export const DEPARTMENT_CREATE_MUTATION = gql`
  mutation createDepartment($input: DepartmentInput!) {
    createDepartment(departmentInput: $input) {
      id
      name
      taxType
      notInUse
      salesNominalCode
      purchaseNominalCode
      code
      paymentTerms {
        edges {
          node {
            amount
            altAmount
            days
            isPercent
            termType
          }
        }
      }
    }
  }
`;
export const DEPARTMENT_UPDATE_MUTATION = gql`
  mutation departmentUpdate($id: ID!, $input: DepartmentInput) {
    updateDepartment(id: $id, departmentInput: $input) {
      id
      name
      taxType
      notInUse
      salesNominalCode
      purchaseNominalCode
      code
      paymentTerms {
        edges {
          node {
            amount
            altAmount
            days
            isPercent
            termType
          }
        }
      }
    }
  }
`;
export const DEPARTMENT_DELETE_MUTATION = gql`
  mutation departmentDelete($departmentId: ID!) {
    deleteDepartment(id: $departmentId) {
      id
    }
  }
`;
export const ALL_INTERNAL_STATUSES_SEARCH_QUERY = gql`
  query allInternalStatusesSearch {
    internalStatuses {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;
export const INTERNAL_STATUS_CREATE_MUTATION = gql`
  mutation internalStatusCreate($name: String!) {
    createInternalStatus(internalstatusInput: { name: $name }) {
      id
      name
    }
  }
`;
export const INTERNAL_STATUS_UPDATE_MUTATION = gql`
  mutation internalStatusUpdate($id: ID!, $name: String!) {
    updateInternalStatus(id: $id, internalstatusInput: { name: $name }) {
      id
      name
    }
  }
`;
export const INTERNAL_STATUS_DELETE_MUTATION = gql`
  mutation internalStatusDelete($internalStatusId: ID!) {
    deleteInternalStatus(id: $internalStatusId) {
      id
    }
  }
`;
export const ALL_MANDATORY_CHARGES_SEARCH_QUERY = gql`
  query allMandatoryChargesSearch {
    mandatoryCharges {
      edges {
        node {
          currencyCode
          dateFrom
          dateTo
          id
          inUse
          name
          perAdult
          perChild
          perInfant
          perItem
          rateTypeCode
        }
      }
    }
  }
`;
export const MANDATORY_CHARGE_CREATE_MUTATION = gql`
  mutation mandatoryChargeCreate(
    $currencyCode: String
    $dateFrom: DateTimeType!
    $dateTo: DateTimeType!
    $inUse: Boolean
    $name: String!
    $perAdult: Float
    $perChild: Float
    $chargeTypeId: ID
    $perInfant: Float
    $perItem: Float
    $rateTypeCode: String
  ) {
    createMandatoryCharge(
      mandatorychargeInput: {
        currencyCode: $currencyCode
        dateFrom: $dateFrom
        dateTo: $dateTo
        inUse: $inUse
        name: $name
        perAdult: $perAdult
        perChild: $perChild
        chargeTypeId: $chargeTypeId
        perInfant: $perInfant
        perItem: $perItem
        rateTypeCode: $rateTypeCode
      }
    ) {
      currencyCode
      dateFrom
      dateTo
      id
      inUse
      name
      perAdult
      perChild
      perInfant
      perItem
      rateTypeCode
    }
  }
`;
export const MANDATORY_CHARGE_UPDATE_MUTATION = gql`
  mutation mandatoryChargeUpdate(
    $id: ID!
    $currencyCode: String
    $dateFrom: DateTimeType!
    $dateTo: DateTimeType!
    $inUse: Boolean
    $name: String!
    $perAdult: Float
    $perChild: Float
    $perInfant: Float
    $perItem: Float
    $chargeTypeId: ID
    $rateTypeCode: String
  ) {
    updateMandatoryCharge(
      id: $id
      mandatorychargeInput: {
        currencyCode: $currencyCode
        dateFrom: $dateFrom
        dateTo: $dateTo
        inUse: $inUse
        name: $name
        perAdult: $perAdult
        perChild: $perChild
        perInfant: $perInfant
        perItem: $perItem
        chargeTypeId: $chargeTypeId
        rateTypeCode: $rateTypeCode
      }
    ) {
      currencyCode
      dateFrom
      dateTo
      id
      inUse
      name
      perAdult
      perChild
      perInfant
      perItem
      rateTypeCode
    }
  }
`;
export const MANDATORY_CHARGE_DELETE_MUTATION = gql`
  mutation mandatoryChargeDelete($mandatoryChargeId: ID!) {
    deleteMandatoryCharge(id: $mandatoryChargeId) {
      id
    }
  }
`;
export const AGENT_GROUPS_SEARCH_QUERY = gql`
  query agentGroupsSearch($filter: AgentGroupFilterType) {
    agentGroups(
      first: 10000
      orderBy: { fieldName: "name", direction: ASC }
      filter: $filter
    ) {
      edges {
        node {
          id
          name
          notInUse
          members {
            totalCount
          }
        }
      }
    }
  }
`;
export const AGENT_GROUPS_LIST_QUERY = gql`
  query agentGroupsList($filter: AgentGroupFilterType) {
    agentGroups(
      first: 10000
      orderBy: { fieldName: "name", direction: ASC }
      filter: $filter
    ) {
      edges {
        node {
          id
          name
          notInUse
        }
      }
    }
  }
`;
export const AGENT_GROUP_SEARCH_QUERY = gql`
  query agentGroupSearch($id: ID!) {
    agentGroup(id: $id) {
      id
      notInUse
      members {
        edges {
          node {
            id
            name
            companyCode
          }
        }
      }
      name
    }
  }
`;
export const AGENT_GROUP_CREATE_MUTATION = gql`
  mutation agentGroupCreate($name: String!, $agentIds: [ID]!, $notInUse: Boolean) {
    createAgentGroup(
      agentgroupInput: { name: $name, agentIds: $agentIds, notInUse: $notInUse }
    ) {
      id
      name
      notInUse
    }
  }
`;
export const AGENT_GROUP_UPDATE_MUTATION = gql`
  mutation agentGroupUpdate($id: ID!, $name: String!, $agentIds: [ID]!, $notInUse: Boolean) {
    updateAgentGroup(
      id: $id
      agentgroupInput: { name: $name, agentIds: $agentIds, notInUse: $notInUse }
    ) {
      id
      name
      notInUse
    }
  }
`;
export const AGENT_GROUP_DELETE_MUTATION = gql`
  mutation agentGroupDelete($agentGroupId: ID!) {
    deleteAgentGroup(id: $agentGroupId) {
      id
    }
  }
`;
export const AGENT_UPDATE_MUTATION = gql`
  mutation agentUpdate(
    $id: ID!
    $name: String!
    $email: String!
    $telephone: String
    $languageId: ID
    $department: String
    $image: String
    $companyCode: String
    $agentType: String
    $city: String
    $region: String
    $state: String
    $postCode: String
    $configSettings: AgentConfigSettingsInput
    $financeSettings: FinanceSettingsInput
    $accessSettings: [AccessSettingInput]
    $customFields: [CustomFieldInput]
    $paymentTerms: [PaymentTermInput]
    $notInUse: Boolean
    $roundDiscounts: Boolean
    $roundMarkups: Boolean
    $address: String
    $notes: String
    $externalDomain: String
  ) {
    updateAgent(
      id: $id
      agentInput: {
        name: $name
        email: $email
        telephone: $telephone
        languageId: $languageId
        department: $department
        image: $image
        companyCode: $companyCode
        agentType: $agentType
        city: $city
        region: $region
        state: $state
        postCode: $postCode
        configSettings: $configSettings
        financeSettings: $financeSettings
        accessSettings: $accessSettings
        customFields: $customFields
        paymentTerms: $paymentTerms
        notInUse: $notInUse
        roundDiscounts: $roundDiscounts
        roundMarkups: $roundMarkups
        address: $address
        notes: $notes
        externalDomain: $externalDomain
      }
    ) {
      id
      name
      telephone
      region
      state
      department
      externalDomain
    }
  }
`;
export const AGENT_CREATE_MUTATION = gql`
  mutation agentCreate(
    $name: String!
    $email: String!
    $telephone: String
    $address: String
    $languageId: ID
    $department: String
    $image: String
    $companyCode: String
    $agentType: String
    $city: String
    $region: String
    $state: String
    $postCode: String
    $notInUse: Boolean
    $roundDiscounts: Boolean
    $roundMarkups: Boolean
    $configSettings: AgentConfigSettingsInput
    $financeSettings: FinanceSettingsInput
    $accessSettings: [AccessSettingInput]
    $notes: String
    $externalDomain: String
  ) {
    createAgent(
      agentInput: {
        name: $name
        email: $email
        telephone: $telephone
        address: $address
        languageId: $languageId
        department: $department
        image: $image
        companyCode: $companyCode
        agentType: $agentType
        city: $city
        region: $region
        state: $state
        postCode: $postCode
        notInUse: $notInUse
        roundDiscounts: $roundDiscounts
        roundMarkups: $roundMarkups
        configSettings: $configSettings
        financeSettings: $financeSettings
        accessSettings: $accessSettings
        notes: $notes
        externalDomain: $externalDomain
      }
    ) {
      id
    }
  }
`;
export const AGENT_DELETE_MUTATION = gql`
  mutation agentDelete($id: ID!) {
    deleteAgent(id: $id) {
      id
    }
  }
`;

export const GET_PRICE_BREAKDOWN_QUERY = gql`
  query getPriceBreakdown(
    $serviceId: ID!
    $agentId: ID
    $department: String
    $ratePlan: String
    $adults: Int!
    $children: Int!
    $infants: Int!
    $qty: Int!
    $itineraryCreationDate: String
    $startDate: DateWithTimeType!
    $endDate: DateWithTimeType!
    $extras: [ItineraryItemExtraInput]
    $yearShift: Int
  ) {
    itemPricing: getPricing(
      serviceId: $serviceId
      agentId: $agentId
      department: $department
      ratePlan: $ratePlan
      adults: $adults
      children: $children
      infants: $infants
      qty: $qty
      itineraryCreationDate: $itineraryCreationDate
      startDate: $startDate
      endDate: $endDate
      extras: $extras
      yearShift: $yearShift
    ) {
      adultBuy
      adultSell
      childBuy
      childSell
      infantBuy
      infantSell
      itemBuy
      itemSell
      rateTypeCode
      ratePlan
      currencySell
      currencySellExchangeRate
      currencyBuy
      currencyBuyExchangeRate
      rateTypeCode
      totalSell
      serviceSell
      discount
      discountId
      discountCost
      discountSell
      businessModel
      supplierCommission
      message
      extras {
        id
        name
        uid
        totalSell
        totalBuy
        isChildOnly
        isInfantOnly
        rateTypeCode
        rateBase
        adults
        children
        infants
        cost
        sell
        percentPrice
        customPricing
        isMandatory
        commission
        discount
        discountCost
        discountSell
        currencyBuyExchangeRate
        currencySellExchangeRate
        ${EXPLAINER_FIELDS}
      }
      serviceNotes {
        id
        description
      }
      ${EXPLAINER_FIELDS}
    }
  }
`;
export const GET_AVAILABLE_EXTRAS_QUERY = gql`
  query getAvailableExtras(
    $serviceId: ID!
    $rsResultServiceId: ID
    $startDate: DateTimeType!
    $endDate: DateTimeType!
    $agentId: ID
    $yearShift: Int
    $department: String
  ) {
    availableExtras(
      serviceId: $serviceId
      rsResultServiceId: $rsResultServiceId
      startDate: $startDate
      endDate: $endDate
      agentId: $agentId
      yearShift: $yearShift
      department: $department
    ) {
      edges {
        node {
          id
          name
          buy
          sell
          currencySell
          currencySellExchange
          currencyBuyExchange
          currencyBuy
          rateBase
          rateTypeCode
          percentPrice
          isMandatory
          isChildOnly
          isInfantOnly
          validFrom
          validTo
          commission
        }
      }
    }
  }
`;
export const GET_FB_TOKEN_QUERY = gql`
  query getGTRoutesFirebaseToken {
    gtRoutesFbToken
  }
`;
export const REMOVE_RECORDS = gql`
  mutation removeRecords($input: [ID!]!) {
    deleteRecords(input: $input)
  }
`;
export const UPDATE_ITI_HEADER = gql`
  mutation updateItineraryHeader($id: ID!, $input: ItineraryHeaderInput!) {
    updateItineraryHeaderInfo(id: $id, itineraryHeaderInput: $input) {
      id
    }
  }
`;

export const ALL_ACCESS_GROUPS = gql`
  query allAccessGroups {
    accessGroups {
      edges {
        node {
          id
          name
          accessSettings {
            groupName
            settingName
            settingValue
          }
          users {
            id
            name
            username
          }
        }
      }
    }
  }
`;
export const UPDATE_VUE_SETTINGS = gql`
  mutation updateVueSettings($input: String!) {
    updateVueSettings(input: $input) {
      id
      vueSettings
    }
  }
`;
export const CREATE_INVOICE_ADJUSTMENT = gql`
  mutation createInvoiceAdjustment($input: InvoiceAdjustmentInput!) {
    createInvoiceAdjustment(input: $input) {
      id
      amount
      vatCode
      vatPercent
      amount
      username
    }
  }
`;
export const PING_IT = gql`
  mutation pingIti($id: ID!, $fingerprint: String) {
    pingItinerary(id: $id, fingerprint: $fingerprint) {
      success
    }
  }
`;
export const CLOSE_IT = gql`
  mutation closeIti($id: ID!, $fingerprint: String) {
    pingItinerary(id: $id, close: true, fingerprint: $fingerprint) {
      success
    }
  }
`;
export const FLIGHT_LOOKUP = gql`
  query flightLookup($flightNo: String, $serviceId: ID, $date: DateWithTimeType!) {
    flight: flightLookup(
      date: $date
      flightNo: $flightNo
      serviceId: $serviceId
    ) {
      flightNo
      arrivalTime
      arrivalPoint
      departureTime
      departurePoint
    }
  }
`;
export const UPDATE_RECORDS_MUTATION = gql`
  mutation batchUpdate($input: UpdateRecordsInput!) {
    updateRecords(input: $input) {
      success
      message
    }
  }
`;

export const ALL_THIRD_PARTY_API_LOGS_QUERY = gql`
  query allThirdPartyApiLogs(
    $after: String
    $sort: SortOrderType
    $first: Int
    $filter: ThirdPartyApiLogFilterType
  ) {
    thirdPartyApiLogs(
      first: $first
      after: $after
      orderBy: $sort
      filter: $filter
    ) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          operation
          httpMethod
          reference
          success
          responseCode
          durationMs
          errorMessage
          requestBody
          responseBody
          createdAt
        }
      }
    }
  }
`;

export const THIRD_PARTY_API_LOGS_TOTAL_COUNT_QUERY = gql`
  query thirdPartyApiLogsTotalCount($filter: ThirdPartyApiLogFilterType) {
    thirdPartyApiLogs(first: 1, filter: $filter) {
      totalCount
    }
  }
`;

// Itinerary Analysis (ITA-07). The query is polled while status is queued/running.
export const REQUEST_ITINERARY_ANALYSIS = gql`
  mutation requestItineraryAnalysis($itineraryId: ID!) {
    requestItineraryAnalysis(itineraryId: $itineraryId) {
      id
      status
      message
    }
  }
`;

// #11503 ITA-22 times and pickup/dropoff points only, no full itinerary save
export const UPDATE_SERVICE_REMARKS = gql`
  mutation updateServiceRemarks(
    $id: ID!
    $services: [ServiceRemarksUpdateInput!]!
  ) {
    updateServiceRemarks(id: $id, services: $services) {
      id
    }
  }
`;

export const ITINERARY_ANALYSIS = gql`
  query itineraryAnalysis($itineraryId: ID!) {
    itineraryAnalysis(itineraryId: $itineraryId) {
      id
      status
      severity
      summary
      error
      createdAt
      finishedAt
      stats {
        narrativeModel
      }
      recommendations {
        text
        findingIds
      }
      findings {
        id
        code
        severity
        section
        date
        serviceIds
        title
        detail
        suggestionType
        evidence
        candidates {
          serviceId
          serviceName
          supplierName
          costBase
          sellBase
          marginDelta
          source
        }
      }
    }
  }
`;



// WEBPACK FOOTER //
// ./src/constants/graphql.js